// The client application question bank (SEP-263, decision D4).
//
// THE SOURCE IS THE FIRM'S OWN FORM. Every section, question, option and
// explanation below comes from `DGW Application Form (Disc/Adv) Individual
// v2.0 - fillable.pdf`, which the firm supplied on 2026-09-18 as the definitive
// reference (SEP-263 section 4). The extracted text is
// `docs/sources/sep-263-application-form-v2.0.txt` and the 378 fields are
// `docs/sources/sep-263-application-form-v2.0-fields.csv`.
//
// # Why the bank lives in the contract, and not in the web app
//
// The server must validate a submission against the same bank that the browser
// rendered, and the server must not import the web app. The precedent is
// `apps/server/src/lib/fact-find/fact-find-form-fields.ts`, the mirror the
// server keeps for exactly that reason. One copy in the contract removes the
// mirror. The SHAPE copies `apps/web/src/lib/fact-find-sections.ts`: a section
// holds questions, and a question holds an id, a kind, options, an explainer
// and a condition.
//
// # FORM VERSION 3.0 ASKS THE CLIENT QUESTIONS OF THE FACT FIND V3
//
// SEP-293.1 adds the client questions of the "Internal Suitability Assessment
// v3" of the firm. That form is the form of the IM, in the words of the IM,
// so the client reads the client words of the "Existing Clients" v6 fact find
// (`fact-find-existing-client.ts`, SEP-288). The questions that only the IM
// answers (sections 7, 8 and 9 of the v3 form, and the calculations) are not
// here. A question whose meaning or options change gets a new id, and the old
// id retires in version 3.0.
//
// # THE WORDING IS DRAFT
//
// The footer of every page of the supplied form reads "DRAFT (pending
// compliance sign-off)". SEP-243 decision D4 built the seam for this, and
// SEP-263 decision D3 applies it: `ComplianceCopyEntry` holds a `(scope, code)`
// pair, `PublishComplianceCopy` supersedes and never edits in place, and THE
// PLACEHOLDER IS CODE. This module IS that placeholder. The scope is
// `application` and the two codes are `form_wording` and `declarations`
// (`compliance-copy.ts`). No row seeds this wording, because a row in the
// register reads as approved wording that nobody approved.
//
// A surface that prints a label from this bank reads
// `resolveClientApplicationWording` first, so a reader learns that Compliance
// has confirmed nothing yet.
//
// # THE COPY IS VERBATIM
//
// Every "What does this mean?" block, every "Why we ask:" block, the three
// investment-objective descriptions of Part 7 and the six risk-profile
// descriptions of Part 8 are compliance-reviewed client-facing copy. An author
// never paraphrases, shortens or re-words one of them. The six risk-profile
// descriptions are NOT repeated here: `fact-find-explainers.ts` states that it
// is the ONE place the firm's risk wording lives, and `riskProfileExplainer`
// gives each body. The two texts agree word for word, with one difference: the
// supplied form spells "specialised" in the AIM IHT description and the
// explainer library spells "specialized". Compliance settles that spelling when
// it publishes the wording.
//
// SEP-321 changes one thing. The risk-profile list of the client page now shows
// three or four short points for each profile, because the full text was too
// long to read in the open list. The full text stays in the library.
//
// # THE IDS ARE FROZEN
//
// A submitted snapshot answers by question id for ever, so a renamed id orphans
// a real client answer. `client-application-frozen-ids.ts` pins the set and
// `client-application-questions.test.ts` fails the build for a drop or a
// rename. A slice may ADD an id. A slice may RETIRE an id with `retiredIn`
// (SEP-293.3): the id stays in the bank, a draft no longer shows it, and an
// older submission still shows its answer. A slice may never drop or rename
// an id.
//
// # ONE DELIBERATE CHANGE FROM THE FORM
//
// Declaration 6c of Part 12 names the Schedule of Charges. SEP-263.39 removes
// that line, because the firm provides the Schedule of Charges with the Mandate
// and the Welcome Pack and not with the application. Declarations 6a, 6b and 6d
// stay. See the comment at `declarations_accepted`.
//
// # PART 11 IS DATA, AND IT IS NOT A CREDENTIAL
//
// The firm settled on 2026-09-21 that the email address and the password are
// the portal identity. The four portal security questions of Part 11 "remain in
// the form for the client to fill out", so they are ORDINARY ANSWERS. They are
// also bank-grade identifiers, so each one carries `excludeFromPrefill` and
// `excludeFromStaffReview`. A later slice reads those two flags: the answer
// never enters the fact-find prefill map (SEP-263.28) and never enters the
// staff review projection (SEP-263.25).
//
// # WHAT IS NOT HERE
//
// Part 13 is a wet signature on paper. The digital journey replaces it with the
// submission act, which carries a date, an instant and an actor, so Part 13 has
// no question. Appendix I and Appendix II are guidance for a non-UK applicant
// and hold no field. The ISA declaration is a separate document of the firm
// (SEP-280). Part 2 asks the two questions that only the declaration needs,
// and the staff review fills the declaration from the answers.
//
// # MONEY
//
// A `currency` question holds a number. A surface formats it in the British
// style, `£{value.toLocaleString('en-GB')}`, which is what `formatCompact()` in
// `OnboardingPipelineView.tsx` does (SEP-263.15). This module invents no
// formatter of its own.

import { isClientApplicationNotApplicableQuestion } from './client-application-not-applicable';
import { PORTAL_SECURITY_ROWS } from './client-application-portal-security';
import {
    COMPLIANCE_COPY_PLACEHOLDER_PREFIX,
    resolveComplianceCopy,
    type ComplianceCopyEntryDto,
    type ComplianceCopyScope,
    type ResolvedComplianceCopy,
} from './compliance-copy';
import {
    CLIENT_APPLICATION_FORM_RULES,
    CLIENT_APPLICATION_VISIBILITY_RULES,
    HEALTH_DETAIL_NEEDED,
} from './client-application-form-rules';
import {
    ADDRESS_HISTORY_NEEDS_SECOND,
    ADDRESS_HISTORY_NEEDS_THIRD,
} from './client-application-address-history';
import {
    CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION,
    CLIENT_APPLICATION_ISA_NEEDS_GIA_NOTICE,
    CLIENT_APPLICATION_ISA_OPTION,
} from './client-application-isa-gia';
import { NOMINATED_REPRESENTATIVE_QUESTIONS } from './client-application-nominated-representative';
import {
    clientApplicationRiskOptions,
    clientApplicationRiskQuestionChange,
} from './client-application-scored-risk';
import {
    EXISTING_CLIENT_ACCOUNT_USAGES,
    EXISTING_CLIENT_ASSET_CLASSES,
    EXISTING_CLIENT_ATR_WORDING,
    EXISTING_CLIENT_HEALTH_OPTIONS,
    EXISTING_CLIENT_INVESTMENT_DECISIONS,
    EXISTING_CLIENT_INVESTMENT_OBJECTIVE_EXPLANATIONS,
    EXISTING_CLIENT_INVESTMENT_OBJECTIVES,
    EXISTING_CLIENT_MONEY_ROW_LABELS,
    EXISTING_CLIENT_WORDING,
    FACT_FIND_WILL_WORDING,
    TRUSTED_CONTACT_STATEMENT,
    existingClientAssetExplainLabel,
    existingClientAssetInvestedLabel,
    existingClientAssetScope,
} from './fact-find-existing-client';
import type { RiskProfile } from './risk-profile';
import type { ClientApplicationFormDefinition } from './client-application-forms';

// ---------------------------------------------------------------------------
// The Compliance copy seam (decision D3)
// ---------------------------------------------------------------------------

/** The copy scope that governs every word of the client application. */
export const CLIENT_APPLICATION_COPY_SCOPE: ComplianceCopyScope = 'application';

/**
 * The two items of the `application` scope.
 *
 * `formWording` covers every label, option and explanation of the bank.
 * `declarations` covers Part 12 alone, because Compliance settles what a client
 * accepts apart from what a client answers.
 */
export const CLIENT_APPLICATION_COPY_CODES = {
    formWording: 'form_wording',
    declarations: 'declarations',
} as const;

/**
 * Resolve both items of the `application` scope against the published register.
 *
 * A surface that prints this bank calls it first. While `isPlaceholder` is
 * true, the surface must state that Compliance has not confirmed the wording.
 */
export function resolveClientApplicationWording(entries: ReadonlyArray<ComplianceCopyEntryDto>): {
    formWording: ResolvedComplianceCopy;
    declarations: ResolvedComplianceCopy;
} {
    return {
        formWording: resolveComplianceCopy(
            CLIENT_APPLICATION_COPY_SCOPE,
            CLIENT_APPLICATION_COPY_CODES.formWording,
            entries,
        ),
        declarations: resolveComplianceCopy(
            CLIENT_APPLICATION_COPY_SCOPE,
            CLIENT_APPLICATION_COPY_CODES.declarations,
            entries,
        ),
    };
}

/**
 * The sentence that a surface prints beside the draft wording.
 *
 * It quotes `COMPLIANCE_COPY_PLACEHOLDER_PREFIX`, so the bank and the copy seam
 * cannot say two different things.
 */
export const CLIENT_APPLICATION_DRAFT_NOTICE = `${COMPLIANCE_COPY_PLACEHOLDER_PREFIX} The footer of every page of the supplied application form reads "DRAFT (pending compliance sign-off)".`;

// ---------------------------------------------------------------------------
// The form versions (SEP-293.3)
// ---------------------------------------------------------------------------

/**
 * Each version of the form, from the oldest to the newest.
 *
 * `2.0` is the paper application of the firm (`DGW Application Form (Disc/Adv)
 * Individual v2.0`), and each submission before SEP-293 used it. `3.0` takes
 * the questions of the Fact Find v3 of the firm. The names come from the
 * documents of the firm. They are values, not identifiers.
 *
 * A question states the versions that hold it (`introducedIn`, `retiredIn`),
 * and it keeps the words (`wordingIn`) and the condition (`showWhenIn`) of an
 * older version. Thus a reader of an old submission sees each answer with
 * the words that the client read.
 */
export const CLIENT_APPLICATION_FORM_VERSIONS = ['2.0', '3.0'] as const;

/** One version of the form. */
export type ClientApplicationFormVersion = (typeof CLIENT_APPLICATION_FORM_VERSIONS)[number];

/** The version that a client fills in now. A draft and a submission use it. */
export const CLIENT_APPLICATION_FORM_VERSION: ClientApplicationFormVersion = '3.0';

/**
 * The version of a submission that records no version.
 *
 * The snapshot got its `formVersion` field with SEP-293.3. Each row from
 * before that holds null, and each one of them used form version 2.0.
 */
export const CLIENT_APPLICATION_UNRECORDED_FORM_VERSION: ClientApplicationFormVersion = '2.0';

/** Whether a value names a version of the form. */
export function isClientApplicationFormVersion(
    value: unknown,
): value is ClientApplicationFormVersion {
    return (CLIENT_APPLICATION_FORM_VERSIONS as readonly unknown[]).includes(value);
}

/**
 * The version of one stored submission.
 *
 * Null and undefined give {@link CLIENT_APPLICATION_UNRECORDED_FORM_VERSION}.
 * A value that names no version is a fault of the data, so the function
 * throws: a guess would show the answers with the wrong words.
 */
export function clientApplicationFormVersionOf(
    stored: string | null | undefined,
): ClientApplicationFormVersion {
    if (stored === null || stored === undefined) return CLIENT_APPLICATION_UNRECORDED_FORM_VERSION;
    if (isClientApplicationFormVersion(stored)) return stored;
    throw new Error(`"${stored}" is not a version of the client application form`);
}

/**
 * Less than 0 when `a` is older than `b`, 0 when they are equal, more than 0
 * otherwise (SEP-293).
 *
 * `versions` is the version line of one form, from the oldest to the newest.
 * The default is the line of the individual form. Each form has its own line,
 * so a version of one form means nothing in the line of another form. A
 * version that the line does not hold is a fault of the bank, so the function
 * throws.
 */
export function compareClientApplicationFormVersions(
    a: string,
    b: string,
    versions: readonly string[] = CLIENT_APPLICATION_FORM_VERSIONS,
): number {
    const left = versions.indexOf(a);
    const right = versions.indexOf(b);
    if (left === -1 || right === -1) {
        throw new Error(
            `"${left === -1 ? a : b}" is not a version of the form (${versions.join(', ')})`,
        );
    }
    return left - right;
}

/**
 * Whether a version is in the span `[introducedIn, retiredIn)`.
 *
 * An absent `introducedIn` means the oldest version, and an absent
 * `retiredIn` means that no version retires the item. A question and an
 * upgrade step both use this span.
 */
export function isInClientApplicationFormVersionSpan(
    span: {
        readonly introducedIn?: string;
        readonly retiredIn?: string;
    },
    version: string,
    versions: readonly string[] = CLIENT_APPLICATION_FORM_VERSIONS,
): boolean {
    if (
        span.introducedIn !== undefined &&
        compareClientApplicationFormVersions(version, span.introducedIn, versions) < 0
    ) {
        return false;
    }
    return (
        span.retiredIn === undefined ||
        compareClientApplicationFormVersions(version, span.retiredIn, versions) < 0
    );
}

/**
 * The calendar date of one instant in London, as `YYYY-MM-DD`.
 *
 * A rule that compares a date with "today" reads this value. The contract
 * never reads the clock: the caller gives the instant, from `clockNow()` for
 * a draft, and from the instant of submission for a snapshot.
 */
export function clientApplicationCalendarDate(at: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(at);
}

// ---------------------------------------------------------------------------
// The types
// ---------------------------------------------------------------------------

/**
 * What a question collects.
 *
 * The values are snake_case, because they are values and not keys. They match
 * the fact-find bank, so a reader of one bank reads the other.
 *
 * `acknowledgement` is the one kind the fact find has no name for: a single
 * tick against a block of wording the client accepts. The form prints five of
 * them, all in Part 12 and Part 7.
 *
 * `tick` is a single tick that states a fact and accepts no wording, for
 * example "all accumulated income" beside a withdrawal amount (SEP-280). It
 * holds `true` or nothing, and a reader prints it as "Yes".
 */
export type ClientApplicationQuestionKind =
    | 'short_text'
    | 'long_text'
    | 'number'
    | 'currency'
    | 'date'
    | 'single_select'
    | 'multi_select'
    | 'yes_no'
    | 'country_select'
    | 'acknowledgement'
    | 'tick';

/**
 * What a `short_text` answer holds when it is not free text.
 *
 * The kind stays `short_text`, so the stored answer, the prefill map and the
 * validator do not change. The format tells the journey which keyboard to
 * open and how to show the typed text: a sort code reads `12-34-56`, and a
 * postcode, an identifier and an IBAN read in capitals.
 */
export type ClientApplicationTextFormat =
    | 'phone'
    | 'email'
    | 'postcode'
    | 'national_insurance_number'
    | 'sort_code'
    | 'account_number'
    | 'iban'
    | 'bic';

/**
 * A text format that applies only while one answer holds a value (SEP-293,
 * rule H12).
 *
 * The National Client Identifier of a UK national is the National Insurance
 * number. A person of another nationality has another identifier, so the
 * format applies only while the nationality is the United Kingdom.
 */
export interface ClientApplicationTextFormatWhen {
    readonly questionId: string;
    readonly equals: readonly string[];
    readonly format: ClientApplicationTextFormat;
}

/** Where the postcode lookup writes each line of an address (decision D7). */
export interface ClientApplicationAddressTargets {
    readonly houseNoName: string;
    readonly street: string;
    readonly townCity: string;
    readonly county: string;
    readonly postcode: string;
    readonly country: string;
}

/**
 * One statement that a client accepts with a tick.
 *
 * A statement is wording, not an answer. The tick is the answer, and the
 * statements are what the tick binds to, so a stored snapshot pins both.
 */
export interface ClientApplicationStatement {
    /** Stable id, frozen with the question ids. */
    readonly id: string;
    /** The form's own words. Never rewrite one. */
    readonly text: string;
}

/** One paragraph of explanation, with the condition that prints it. */
export interface ClientApplicationParagraph {
    /** The form's own words. Never rewrite one. */
    readonly text: string;
    /** Absent: the paragraph always prints with its question. */
    readonly showWhen?: ClientApplicationShowWhen;
}

/** A condition that opens a question. */
export interface ClientApplicationCondition {
    readonly questionId: string;
    /**
     * A STRING answer matches when `equals` holds it. An ARRAY answer matches
     * when it INCLUDES any entry of `equals`. This is the one rule of the
     * fact-find bank, and it does not change here.
     */
    readonly equals: readonly string[];
}

/**
 * The condition of a question. A list holds when EACH condition of it holds,
 * so a list is an AND.
 */
export type ClientApplicationShowWhen =
    | ClientApplicationCondition
    | readonly ClientApplicationCondition[];

/**
 * The name of a computed rule that opens a question.
 *
 * The bank holds the rule under this name, in `visibilityRules`. A rule reads
 * more than one answer, or it compares an answer with today. A name that the
 * bank does not hold keeps the question hidden.
 */
export type ClientApplicationRuleId = string;

/** What a rule of the bank can read beside the answers. */
export interface ClientApplicationRuleContext {
    /** The version of the form that the answers belong to. */
    readonly version: string;
    /**
     * The date of today, `YYYY-MM-DD`, from the caller. A draft gives the
     * date of `clockNow()`, and a snapshot gives the date of submission.
     * Null when the caller gives no date. A rule that needs the date must
     * then give its safe answer: a visibility rule keeps its question shown,
     * and a form rule gives no error.
     */
    readonly today: string | null;
}

/** A computed rule that opens a question. It gives true when the question shows. */
export type ClientApplicationVisibilityRule = (
    answers: Readonly<Record<string, unknown>>,
    context: ClientApplicationRuleContext,
) => boolean;

/** One error that a form rule gives. The engine finds the section of the question. */
export interface ClientApplicationRuleError {
    readonly questionId: string;
    readonly message: string;
}

/** What a form rule can read beside the answers. */
export interface ClientApplicationFormRuleContext extends ClientApplicationRuleContext {
    /**
     * True when the caller checks that the form is complete: a step forward
     * and the submission. A save gives false. A rule about a whole set of
     * answers, for example the span of an address history, checks only when
     * this is true, because a client saves a half-filled section.
     */
    readonly requireComplete: boolean;
}

/**
 * A rule that reads more than one answer (SEP-293.3).
 *
 * The per-question checks read one answer each. A form rule reads the whole
 * map, for example "this date is before that date". The validator runs each
 * rule after the per-question checks. It keeps an error only for a visible
 * question that has no error of its own.
 */
export interface ClientApplicationFormRule {
    /** A stable name, for a test and for a reader of a log. */
    readonly id: string;
    readonly check: (
        answers: Readonly<Record<string, unknown>>,
        context: ClientApplicationFormRuleContext,
    ) => readonly ClientApplicationRuleError[];
}

/**
 * The words of a question in one older version of the form.
 *
 * Each field replaces the field of the same name. A field that is absent keeps
 * the current words. Use it only when the meaning and the option set stay the
 * same: `options` holds the older label of each option, in the same order.
 * When the meaning or the option set changes, retire the question and add a
 * new id, so that a stored answer always means what its label says.
 */
export interface ClientApplicationWording {
    readonly label?: string;
    readonly explainer?: string;
    readonly options?: readonly string[];
    readonly optionNotes?: Readonly<Record<string, string>>;
    readonly placeholder?: string;
}

/**
 * The condition of a question in one older version of the form.
 *
 * It replaces BOTH `showWhen` and `showWhenRule` in that version. An empty
 * object means that the question always shows in that version. `required`
 * replaces the `required` flag in that version when it is given: false means
 * that the older version did not need an answer (SEP-293.5).
 */
export interface ClientApplicationVisibility {
    readonly showWhen?: ClientApplicationShowWhen;
    readonly showWhenRule?: ClientApplicationRuleId;
    readonly required?: boolean;
}

/** One question of the client application. */
export interface ClientApplicationQuestion {
    /** Stable id. A submitted snapshot answers by this id for ever. */
    readonly id: string;
    readonly kind: ClientApplicationQuestionKind;
    /** The form's own label. */
    readonly label: string;
    /**
     * The form's own explanatory copy, verbatim. It is the "What does this
     * mean?" block, the "Why we ask:" block or the instruction that sits under
     * the field on the paper form.
     */
    readonly explainer?: string;
    /**
     * More paragraphs of the form's own copy, printed after the
     * {@link explainer}, in this order (SEP-293.8). A paragraph with a
     * condition prints only while the condition holds, so one question can
     * carry the copy of two variants of a paper form.
     */
    readonly explainerParagraphs?: readonly ClientApplicationParagraph[];
    /** The option labels, in the order the form prints them. */
    readonly options?: readonly string[];
    /** A note under one option, keyed by the option LABEL. The form's own copy. */
    readonly optionNotes?: Readonly<Record<string, string>>;
    /**
     * The options of a `multi_select` that the client can choose together.
     *
     * When the bank sets this list, an option outside it stands alone. A
     * choice outside the list clears every other choice, and a choice inside
     * the list clears each choice outside it. The journey applies the rule
     * when the client presses an option (`chooseClientApplicationOption`),
     * and the validator refuses every other mix. When the bank does not set
     * the list, the client can choose any mix.
     */
    readonly combinableOptions?: readonly string[];
    /**
     * The options of a `multi_select` that one option brings with it, keyed
     * by the option LABEL (SEP-293.7).
     *
     * A press on the key option also chooses each option of its list. While
     * the key option stays chosen, the client cannot remove an option of its
     * list. A press that removes the key option keeps the options of its
     * list. Each option of a list must go together with its key option under
     * {@link combinableOptions}.
     */
    readonly impliedOptions?: Readonly<Record<string, readonly string[]>>;
    /**
     * A notice that the journey prints under the control while the client
     * has chosen one option, keyed by the option LABEL (SEP-293.7). It is the
     * form's own copy.
     */
    readonly chosenOptionNotices?: Readonly<Record<string, string>>;
    /** The statements that an `acknowledgement` binds to. */
    readonly statements?: readonly ClientApplicationStatement[];
    readonly placeholder?: string;
    /**
     * Whether the journey blocks a submission while the answer is blank. A
     * bank that sets {@link ClientApplicationBank.requireEveryAnswer} gives
     * this flag to each question that does not opt out with {@link optional}.
     */
    readonly required?: true;
    /**
     * The question can stay blank, in a bank that requires every answer. A
     * consent, the portal security answers and a tick opt out. A tick opts
     * out without this flag.
     */
    readonly optional?: true;
    /**
     * Not every client has an answer to this question, for example a second
     * nationality or a home telephone number. The version view adds a
     * "Not applicable" tick beside the question
     * ({@link clientApplicationNotApplicableId}). The tick completes the
     * question, and a staff reader sees "Not applicable" and not a blank.
     */
    readonly notApplicable?: true;
    /**
     * The id of the question that this "Not applicable" tick completes. Only
     * the version view sets it, on the tick that it adds.
     */
    readonly notApplicableFor?: string;
    /**
     * The id of a `tick` question that waives {@link required}.
     *
     * While the client ticks that question, a blank answer here is complete.
     * The regular withdrawal amount carries it: a client who takes all of the
     * accumulated income names no amount (SEP-280).
     */
    readonly requiredUnlessTicked?: string;
    /**
     * Draw a select as a DROPDOWN, whatever the length of its list. A
     * dropdown keeps a long option note out of the page: the note shows
     * inside the open list, against the option it belongs to.
     */
    readonly dropdown?: true;
    /** The form's own sub-heading, where a part carries one. */
    readonly group?: string;
    /**
     * The journey draws this question in the same row as the question before
     * it, as the paper form draws "Figure" and "Personal % ownership" in two
     * columns (SEP-340 item 12).
     */
    readonly besidePrevious?: true;
    /**
     * A short unit that the journey prints after the box and the review prints
     * after the answer, for example `%` (SEP-340 item 12).
     */
    readonly suffix?: string;
    /**
     * The question is shown only when the condition holds. A list holds when
     * each condition of it holds.
     */
    readonly showWhen?: ClientApplicationShowWhen;
    /**
     * The question is shown only when the named rule of the bank holds. When
     * the question also has a {@link showWhen}, both must hold.
     */
    readonly showWhenRule?: ClientApplicationRuleId;
    /**
     * The condition in an older version, where it differs from the current
     * condition. The key is the version.
     */
    readonly showWhenIn?: Partial<Record<string, ClientApplicationVisibility>>;
    /**
     * The first version that holds the question. Absent means the oldest
     * version. The version is a version of the form that holds the bank
     * (SEP-293).
     */
    readonly introducedIn?: string;
    /**
     * The first version that does NOT hold the question. Absent means that
     * each later version holds it. A retired question stays in the bank, so
     * an older submission still shows its answer with its words. A draft
     * never shows it, and the prune drops its answer.
     */
    readonly retiredIn?: string;
    /**
     * The words in an older version, where they differ from the current
     * words. The key is the version, and each version that differs needs its
     * own entry.
     */
    readonly wordingIn?: Partial<Record<string, ClientApplicationWording>>;
    /** What a `short_text` answer holds when it is not free text. */
    readonly textFormat?: ClientApplicationTextFormat;
    /**
     * A format that replaces {@link textFormat} while one answer holds a
     * value (SEP-293, rule H12). Read the format with
     * {@link clientApplicationTextFormat}.
     */
    readonly textFormatWhen?: ClientApplicationTextFormatWhen;
    /**
     * Which questions the address lookup fills.
     *
     * It sits on the FIRST question of an address group, because the lookup
     * fills the fields BELOW it and a control that fills the fields above it
     * reads backwards. The targets name every field of the group, so the
     * lookup writes the whole address whatever question carries the key.
     */
    readonly addressLookup?: ClientApplicationAddressTargets;
    /**
     * The option list is not static: the journey supplies it at render time.
     * Only the proposed investment manager carries it, and the list is the live
     * roster of the firm (SEP-263.3 and SEP-263.4).
     */
    readonly optionsFromRoster?: 'investment_managers';
    /**
     * The start page already collected this answer, so the journey seeds it
     * from the application row and never asks it a second time (SEP-263.43).
     * The question STAYS in the bank: a submitted snapshot answers by id for
     * ever, and the staff review, the prefill map and the identity read all
     * name the id.
     */
    readonly settledAtStart?: true;
    /** Special-category data under Article 9, or a bank-grade identifier. */
    readonly sensitive?: true;
    /** The answer never enters the fact-find prefill map (SEP-263.28). */
    readonly excludeFromPrefill?: true;
    /** The answer never enters the staff review projection (SEP-263.25). */
    readonly excludeFromStaffReview?: true;
}

/** One section of the client application. One section is one part of the form. */
export interface ClientApplicationSection {
    /** Stable section id. */
    readonly id: string;
    /** The part number the form prints, or null for the Service Type block. */
    readonly part: number | null;
    /** The form's own heading. */
    readonly title: string;
    /**
     * The short name the progress stepper prints.
     *
     * A stepper of thirteen chips has no room for "Risk tolerance & capacity
     * for loss", and "Part 8" names nothing at all. This is the section in one
     * or two words, and it defaults to {@link title}.
     */
    readonly stepLabel?: string;
    /** The form's own boxed introduction, verbatim. */
    readonly intro?: string;
    /** Prose the form prints that asks for no answer. */
    readonly notes?: readonly ClientApplicationStatement[];
    readonly questions: readonly ClientApplicationQuestion[];
}

// ---------------------------------------------------------------------------
// Shared option lists, in the order the form prints them
// ---------------------------------------------------------------------------

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Other'] as const;

/** Part 8 prints this five-point scale under four statements. */
const AGREEMENT_SCALE = [
    'Strongly disagree',
    'Disagree',
    'Neither agree nor disagree',
    'Agree',
    'Strongly agree',
] as const;

/** Part 8 prints this five-point scale twice: for income and for liabilities. */
const TEN_YEAR_CHANGE_SCALE = [
    'Decrease significantly',
    'Decrease somewhat',
    'Remain stable',
    'Increase somewhat',
    'Increase significantly',
] as const;

/** Part 7 prints this three-point scale against each of six products. */
const PRODUCT_EXPERIENCE_SCALE = ['None', '1-3 yrs', '3+ yrs'] as const;

/**
 * The six risk profiles of Part 8, as the form labels them.
 *
 * The en dash of "Moderately – Low Risk" is the form's own character. The map
 * binds each label to the `RiskProfile` key, so the description comes from the
 * one place the firm's risk wording lives.
 */
const INTENDED_RISK_PROFILE_LABELS: ReadonlyArray<{ label: string; profile: RiskProfile }> = [
    { label: 'Low Risk', profile: 'low' },
    { label: 'Moderately – Low Risk', profile: 'moderately_low' },
    { label: 'Moderate Risk', profile: 'moderate' },
    { label: 'Moderately High Risk', profile: 'moderately_high' },
    { label: 'High Risk', profile: 'high' },
    { label: 'AIM IHT', profile: 'aim_iht' },
];

/** The form's own last option of the intended risk profile block. */
const INTENDED_RISK_PROFILE_UNSURE =
    'I am unsure — I would like to discuss my risk profile with my Investment Manager';

const INTENDED_RISK_PROFILE_OPTIONS: readonly string[] = [
    ...INTENDED_RISK_PROFILE_LABELS.map((entry) => entry.label),
    INTENDED_RISK_PROFILE_UNSURE,
];

/**
 * The short points under each risk profile (SEP-321).
 *
 * The full description of each profile is long and technical, and it sat inside
 * the open list. Each profile now has three or four short points, one on each
 * line. They only restate what the full description says: they add no loss
 * figure and no allocation that the description does not hold. The full text
 * stays in the explainer library for the staff reviewer.
 */
const INTENDED_RISK_PROFILE_POINTS: Readonly<Record<string, readonly string[]>> = {
    'Low Risk': [
        'Protects your money first',
        'Cash and high-quality government bonds',
        'Modest, steady growth',
        'Returns follow interest rates',
    ],
    'Moderately – Low Risk': [
        'Returns above cash, below shares',
        'Up to 30% in shares, through funds only',
        'Mostly bonds and cash',
        'Moderate losses can happen',
    ],
    'Moderate Risk': [
        'Accepts short-term losses for higher long-term returns',
        'Up to 70% in shares',
        'Big losses in extreme markets can take years to recover',
        'Often chosen for Income, Growth and Balanced goals',
    ],
    'Moderately High Risk': [
        'High tolerance for risk',
        'Up to 100% in shares',
        'Can hold smaller companies',
        'Significant losses can happen, and recovery can take years',
    ],
    'High Risk': [
        'Aims for the most long-term growth',
        'Mostly shares, including smaller companies',
        'Value can swing a lot',
        'Substantial losses can happen',
    ],
    'AIM IHT': [
        'Aims to reduce Inheritance Tax (IHT)',
        'Invests in qualifying companies on AIM, a London market for smaller companies',
        'Usually 20 to 30 companies',
        'Very high risk, and holdings can be hard to sell',
    ],
};

const INTENDED_RISK_PROFILE_NOTES: Readonly<Record<string, string>> = Object.fromEntries(
    INTENDED_RISK_PROFILE_LABELS.map((entry) => [
        entry.label,
        (INTENDED_RISK_PROFILE_POINTS[entry.label] ?? []).join('\n'),
    ]),
);

/** The address lookup of the current residential address (decision D7). */
const CURRENT_ADDRESS_TARGETS: ClientApplicationAddressTargets = {
    houseNoName: 'current_address_house_no_name',
    street: 'current_address_street',
    townCity: 'current_address_town_city',
    county: 'current_address_county',
    postcode: 'current_address_postcode',
    country: 'current_address_country',
};

/** The address lookup of the previous residential address (decision D7). */
const PREVIOUS_ADDRESS_TARGETS: ClientApplicationAddressTargets = {
    houseNoName: 'previous_address_house_no_name',
    street: 'previous_address_street',
    townCity: 'previous_address_town_city',
    county: 'previous_address_county',
    postcode: 'previous_address_postcode',
    country: 'previous_address_country',
};

/** The address lookup of the third residential address (SEP-293.5). */
const THIRD_ADDRESS_TARGETS: ClientApplicationAddressTargets = {
    houseNoName: 'third_address_house_no_name',
    street: 'third_address_street',
    townCity: 'third_address_town_city',
    county: 'third_address_county',
    postcode: 'third_address_postcode',
    country: 'third_address_country',
};

/**
 * The condition of the previous address in form version 2.0 (SEP-285). In
 * 2.0 a yes or no question opened it, and no field of it needed an answer.
 */
const PREVIOUS_ADDRESS_IN_2_0 = {
    '2.0': {
        showWhen: { questionId: 'lived_at_current_address_3_years', equals: ['No'] },
        required: false,
    },
} as const;

// ---------------------------------------------------------------------------
// The sections. One section is one part of the form, in the form's own order.
// ---------------------------------------------------------------------------

/**
 * The seven account types the firm opens.
 *
 * The paper form of v2.0 names two, because it is the form of an INDIVIDUAL
 * account. The firm settled the full list on 2026-09-22, and the journey asks
 * it once, beside the service type, because the account type decides which
 * later questions and which supplementary form apply.
 *
 * The notes are OUR OWN plain-language copy and not the firm's form copy, so
 * they are written in ASD-STE100. The ISA note keeps the two facts the form
 * states: the tax treatment and the annual limit.
 */
const ACCOUNT_TYPE_OPTIONS = [
    'Individual General Investment',
    'ISA',
    'Joint',
    'SIPP',
    'Company',
    'Trust / Charity',
    // SEP-293: the firm writes "SSAS" (a small self-administered scheme). The
    // option was "SASS" before. A stored draft reads "SSAS"
    // (`upgradeClientApplicationAnswers`), and a snapshot keeps the label
    // that the client chose.
    'SSAS',
] as const;

const ACCOUNT_TYPE_NOTES: Readonly<Record<string, string>> = {
    'Individual General Investment':
        'An account in your name, with no special tax treatment. You pay tax on the income and on the gains. We give you the figures that your tax return needs. We do not give tax advice.',
    ISA: 'A tax-efficient account for a UK resident. You pay no UK income tax and no UK capital gains tax on the money in the account. The limit is £20,000 for each tax year, across all of your ISAs. If you select an ISA, we ask you two more questions. Your answers fill the ISA declaration that you sign.',
    Joint: 'One account for two holders. Each holder completes an application, and each holder can give us instructions.',
    SIPP: 'A self-invested personal pension. We manage the investments. Your pension provider holds the account.',
    Company:
        'An account for a limited company. We ask for the company documents, and for the details of each person who controls the company.',
    'Trust / Charity':
        'An account for a trust or for a charity. We ask for the trust deed or for the charity registration, and for the details of each trustee.',
    SSAS: 'A small self-administered scheme, which is a pension scheme for the directors of one company. We manage the investments. The trustees hold the account.',
};

const SERVICE_TYPE_SECTION: ClientApplicationSection = {
    id: 'service_type',
    // The form prints this block on page 2 above Part 1, and it carries no
    // part number of its own.
    part: null,
    // The start page collects the email address and the password and nothing
    // else, because an account is the only thing a visitor needs before the
    // form opens. Every CHOICE about the service belongs in the form, where
    // the client reads the note against each option and changes their mind
    // freely until they submit. The section id does not change: a stored draft
    // resumes at it.
    title: 'Service and account',
    stepLabel: 'Service',
    questions: [
        {
            id: 'service_type',
            kind: 'single_select',
            label: 'Service type',
            required: true,
            // A dropdown, because each note is a paragraph. Two paragraphs
            // above the next question push the form down the page, and a
            // reader loses the question. The note belongs against its option,
            // inside the open list.
            dropdown: true,
            options: [
                'Discretionary Managed service',
                'Advisory service',
                'Execution Only service',
            ],
            optionNotes: {
                'Discretionary Managed service':
                    'We make the investment decisions for you, and we keep to the objectives and the risk profile that we agree with you. You do not approve each transaction. We send you reports at regular intervals, and we tell you about each important event.',
                'Advisory service':
                    'We give you personal recommendations, but each decision is yours. We do nothing to your portfolio without your instruction. Select this service if you want to stay close to your portfolio, and if you are content to make the final decision. Some recommendations are time-critical, and you must act on these quickly.',
                'Execution Only service':
                    'We carry out the transactions that you instruct. We give you no advice, and we do not assess whether an investment is suitable for you. Where the rules require it, we check only that the investment is appropriate for you. Each decision is yours, and we do nothing to your portfolio without your instruction.',
            },
        },
        {
            // The account type moved here from Part 5 on 2026-09-22. It sits
            // beside the service type, because the two answers together
            // decide the shape of the account. The id is FROZEN, so it keeps
            // the name that Part 5 gave it.
            //
            // The client can choose more than one type (SEP-280), because
            // many clients open a General Investment Account and an ISA
            // together. These two are the ONLY types that go together (the
            // firm, 2026-09-28). Each other type is chosen alone. An
            // application saved before SEP-280 holds one string, and
            // `upgradeClientApplicationAnswers` reads it as a list of one.
            id: 'account_types',
            kind: 'multi_select',
            label: 'Account type',
            required: true,
            dropdown: true,
            options: [...ACCOUNT_TYPE_OPTIONS],
            optionNotes: ACCOUNT_TYPE_NOTES,
            combinableOptions: ['Individual General Investment', 'ISA'],
            // The firm opens an ISA only beside a General Investment Account
            // (SEP-293.7). A press on ISA also ticks the General Investment
            // Account, and the notice is the firm's own words.
            impliedOptions: {
                [CLIENT_APPLICATION_ISA_OPTION]: [CLIENT_APPLICATION_GENERAL_INVESTMENT_OPTION],
            },
            chosenOptionNotices: {
                [CLIENT_APPLICATION_ISA_OPTION]: CLIENT_APPLICATION_ISA_NEEDS_GIA_NOTICE,
            },
        },
        {
            // The paper form reads "If known, please provide the name of your
            // preferred Investment Manager". The firm made the field MANDATORY
            // and removed every "no preference" option (SEP-263.3 and
            // SEP-263.4), and it settled the label on 2026-09-21: the wording
            // that implies a choice is gone. The option list is the live roster
            // of investment managers, which slice 5 supplies.
            id: 'preferred_investment_manager',
            kind: 'single_select',
            label: 'Your proposed Investment Manager',
            required: true,
            dropdown: true,
            optionsFromRoster: 'investment_managers',
        },
    ],
};

const PERSONAL_INFORMATION_SECTION: ClientApplicationSection = {
    id: 'personal_information',
    part: 1,
    title: 'Personal information',
    stepLabel: 'Personal',
    intro: 'This section is to be completed by the sole applicant of an individual account.',
    questions: [
        {
            id: 'title',
            kind: 'single_select',
            label: 'Title',
            required: true,
            options: [...TITLE_OPTIONS],
        },
        {
            id: 'title_other',
            kind: 'short_text',
            label: 'Other (please specify)',
            showWhen: { questionId: 'title', equals: ['Other'] },
        },
        { id: 'surname', kind: 'short_text', label: 'Surname', required: true },
        { id: 'forenames', kind: 'short_text', label: 'Forename(s)', required: true },
        { id: 'date_of_birth', kind: 'date', label: 'Date of Birth', required: true },
        {
            id: 'country_of_birth',
            kind: 'country_select',
            label: 'Country of Birth',
            required: true,
        },
        { id: 'town_of_birth', kind: 'short_text', label: 'Town of birth', required: true },

        {
            id: 'current_address_house_no_name',
            kind: 'short_text',
            label: 'House no./name',
            required: true,
            group: 'Current residential address',
            addressLookup: CURRENT_ADDRESS_TARGETS,
        },
        {
            id: 'current_address_street',
            kind: 'short_text',
            label: 'Street',
            group: 'Current residential address',
        },
        {
            id: 'current_address_town_city',
            kind: 'short_text',
            label: 'Town/City',
            required: true,
            group: 'Current residential address',
        },
        {
            id: 'current_address_county',
            kind: 'short_text',
            label: 'County',
            group: 'Current residential address',
        },
        {
            id: 'current_address_postcode',
            kind: 'short_text',
            label: 'Postcode',
            textFormat: 'postcode',
            required: true,
            group: 'Current residential address',
        },
        {
            id: 'current_address_country',
            kind: 'country_select',
            label: 'Country',
            required: true,
            group: 'Current residential address',
        },

        {
            // The client gives the date of the move to each address, and an
            // address ends on the day that the next one starts (SEP-293.5).
            // When the current address covers less than 36 months, the
            // previous address opens. The rules are in
            // `client-application-address-history.ts`.
            id: 'current_address_moved_in_date',
            kind: 'date',
            label: 'When did you move in to this address?',
            required: true,
            introducedIn: '3.0',
            group: 'Current residential address',
        },

        {
            // Form version 2.0 gated the previous address with this question
            // (SEP-285). Version 3.0 asks for a move-in date for each address
            // instead (SEP-293.5), so this question is retired.
            id: 'lived_at_current_address_3_years',
            kind: 'yes_no',
            label: 'Have you lived at your current address for 3 years or more?',
            required: true,
            retiredIn: '3.0',
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_house_no_name',
            kind: 'short_text',
            label: 'House no./name',
            required: true,
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
            addressLookup: PREVIOUS_ADDRESS_TARGETS,
        },
        {
            id: 'previous_address_street',
            kind: 'short_text',
            label: 'Street',
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_town_city',
            kind: 'short_text',
            label: 'Town/City',
            required: true,
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_county',
            kind: 'short_text',
            label: 'County',
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_postcode',
            kind: 'short_text',
            label: 'Postcode',
            textFormat: 'postcode',
            required: true,
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_country',
            kind: 'country_select',
            label: 'Country',
            required: true,
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            showWhenIn: PREVIOUS_ADDRESS_IN_2_0,
            group: 'Previous residential address',
        },
        {
            id: 'previous_address_moved_in_date',
            kind: 'date',
            label: 'When did you move in to this address?',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_SECOND,
            group: 'Previous residential address',
        },
        {
            // Retired in 3.0: the move-in dates give the time at each address.
            id: 'previous_address_tenure',
            kind: 'short_text',
            label: 'How long did you live at your previous address?',
            showWhen: { questionId: 'lived_at_current_address_3_years', equals: ['No'] },
            retiredIn: '3.0',
            group: 'Previous residential address',
        },

        // The third address is the last one. It opens when the current and
        // the previous address cover less than 36 months. The form has no
        // fourth address: a form rule refuses a history that is still short.
        {
            id: 'third_address_house_no_name',
            kind: 'short_text',
            label: 'House no./name',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
            addressLookup: THIRD_ADDRESS_TARGETS,
        },
        {
            id: 'third_address_street',
            kind: 'short_text',
            label: 'Street',
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },
        {
            id: 'third_address_town_city',
            kind: 'short_text',
            label: 'Town/City',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },
        {
            id: 'third_address_county',
            kind: 'short_text',
            label: 'County',
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },
        {
            id: 'third_address_postcode',
            kind: 'short_text',
            label: 'Postcode',
            textFormat: 'postcode',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },
        {
            id: 'third_address_country',
            kind: 'country_select',
            label: 'Country',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },
        {
            id: 'third_address_moved_in_date',
            kind: 'date',
            label: 'When did you move in to this address?',
            required: true,
            introducedIn: '3.0',
            showWhenRule: ADDRESS_HISTORY_NEEDS_THIRD,
            group: 'Third residential address',
        },

        {
            id: 'mobile_number',
            kind: 'short_text',
            label: 'Mobile number',
            textFormat: 'phone',
            group: 'Contact details',
        },
        {
            id: 'home_number',
            kind: 'short_text',
            label: 'Home number',
            textFormat: 'phone',
            group: 'Contact details',
        },
        {
            id: 'business_number',
            kind: 'short_text',
            label: 'Business number',
            textFormat: 'phone',
            group: 'Contact details',
        },
        {
            // The start page collects the email address, and it is the portal
            // identity of the client (SEP-263.43). The journey seeds the
            // answer from the application row and does not ask it again. The
            // question stays in the bank, because the identity read, the
            // prefill map and the staff review all name this id.
            id: 'email_address',
            kind: 'short_text',
            label: 'Email address',
            textFormat: 'email',
            required: true,
            settledAtStart: true,
            group: 'Contact details',
        },

        {
            // Form version 2.0 took the nationality as free text. Version 3.0
            // takes a country from the list (SEP-293.4), so a stored answer
            // of this id is text and an answer of the next id is a code.
            id: 'nationality',
            kind: 'short_text',
            label: 'Nationality',
            required: true,
            explainer: 'If a dual national, please provide both nationalities.',
            retiredIn: '3.0',
            group: 'Nationality & tax status',
        },
        {
            id: 'country_of_nationality',
            kind: 'country_select',
            label: 'Nationality',
            required: true,
            introducedIn: '3.0',
            group: 'Nationality & tax status',
        },
        {
            id: 'second_country_of_nationality',
            kind: 'country_select',
            label: 'Second nationality (if you hold dual nationality)',
            introducedIn: '3.0',
            group: 'Nationality & tax status',
        },
        {
            id: 'national_client_identifier',
            kind: 'short_text',
            label: 'National Client Identifier / National Insurance Number (e.g. AA123456A)',
            wordingIn: {
                '2.0': {
                    label: 'National Client Identifier (e.g. AA123456A)',
                    explainer:
                        'For your National Client Identifier see Appendix II — if a UK national, use your National Insurance number (format AA123456A).',
                },
            },
            textFormat: 'national_insurance_number',
            required: true,
            explainer:
                'If you are a UK national, use your National Insurance number (format AA123456A).',
            group: 'Nationality & tax status',
        },

        // The form prints three tax-residency rows and a free-text row for the
        // reason a Tax Identification Number is missing. The bank keeps the
        // three rows, because the snapshot must hold what the client wrote in
        // each box.
        {
            id: 'tax_residency_1_country',
            kind: 'country_select',
            label: 'Tax residency and citizenship — country 1',
            required: true,
            explainer: 'Tax residency and citizenship (if more than one, provide all countries)',
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_1_tax_resident',
            kind: 'yes_no',
            label: 'Country 1 — Tax Resident',
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_1_citizen',
            kind: 'yes_no',
            label: 'Country 1 — Citizen',
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_1_tin',
            kind: 'short_text',
            label: 'Country 1 — Tax Identification Number (if applicable)',
            group: 'Nationality & tax status',
        },
        {
            // Rows 2 and 3 are optional (SEP-286). The client opens a row with
            // a yes or no question. `upgradeClientApplicationAnswers` answers
            // the question Yes for a draft that already fills the row.
            id: 'has_tax_residency_2',
            kind: 'yes_no',
            label: 'Are you tax resident or a citizen of another country?',
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_2_country',
            kind: 'country_select',
            label: 'Tax residency and citizenship — country 2',
            showWhen: { questionId: 'has_tax_residency_2', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_2_tax_resident',
            kind: 'yes_no',
            label: 'Country 2 — Tax Resident',
            showWhen: { questionId: 'has_tax_residency_2', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_2_citizen',
            kind: 'yes_no',
            label: 'Country 2 — Citizen',
            showWhen: { questionId: 'has_tax_residency_2', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_2_tin',
            kind: 'short_text',
            label: 'Country 2 — Tax Identification Number (if applicable)',
            showWhen: { questionId: 'has_tax_residency_2', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'has_tax_residency_3',
            kind: 'yes_no',
            label: 'Are you tax resident or a citizen of a third country?',
            showWhen: { questionId: 'has_tax_residency_2', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_3_country',
            kind: 'country_select',
            label: 'Tax residency and citizenship — country 3',
            showWhen: { questionId: 'has_tax_residency_3', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_3_tax_resident',
            kind: 'yes_no',
            label: 'Country 3 — Tax Resident',
            showWhen: { questionId: 'has_tax_residency_3', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_3_citizen',
            kind: 'yes_no',
            label: 'Country 3 — Citizen',
            showWhen: { questionId: 'has_tax_residency_3', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_residency_3_tin',
            kind: 'short_text',
            label: 'Country 3 — Tax Identification Number (if applicable)',
            showWhen: { questionId: 'has_tax_residency_3', equals: ['Yes'] },
            group: 'Nationality & tax status',
        },
        {
            id: 'tax_identification_number_unavailable_reason',
            kind: 'long_text',
            label: 'If a country listed above issues Tax Identification Numbers and you cannot provide yours, please tell us why:',
            group: 'Nationality & tax status',
        },
        {
            id: 'us_person',
            kind: 'yes_no',
            label: 'Are you a US Person?',
            required: true,
            explainer:
                "What does this mean? A 'US Person' broadly includes US citizens (including dual citizens), US tax residents and green-card holders. US tax rules require specific forms (a W-9 for US Persons, or a W-8BEN for others receiving US-source income) — where relevant, we will provide the appropriate form to you separately.",
            group: 'Nationality & tax status',
        },
    ],
};

const PERSONAL_CIRCUMSTANCES_SECTION: ClientApplicationSection = {
    id: 'personal_circumstances',
    part: 2,
    title: 'Personal circumstances',
    stepLabel: 'Circumstances',
    notes: [
        {
            id: 'isa_considerations_note',
            text: 'Please note, the following points are for you to consider and share with us, so we are aware. Dowgate Wealth is not a tax advisor or specialist. Please leave blank if not applicable.',
        },
    ],
    questions: [
        {
            // SEP-293.1. Form version 3.0 adds the option "Not working" of the
            // Fact Find v3, in the words of the v6 form. Each answer of
            // version 2.0 is still an option, and it means the same, so the id
            // stays.
            id: 'employment_status',
            kind: 'single_select',
            label: 'Employment status',
            required: true,
            options: [
                'Employed',
                'Self-employed',
                'Director',
                EXISTING_CLIENT_WORDING.employmentNotWorking,
                'Retired',
                'Other',
            ],
            group: 'Employment status',
        },
        {
            id: 'job_title',
            kind: 'short_text',
            label: 'Job title (if relevant)',
            group: 'Employment status',
        },
        {
            // SEP-293.1. The Fact Find v3 asks for the occupation AND the
            // employer. The words are those of the fact find.
            id: 'employer_name',
            kind: 'short_text',
            label: 'Employer name',
            introducedIn: '3.0',
            group: 'Employment status',
        },
        {
            id: 'employment_status_other',
            kind: 'short_text',
            label: "If 'Other', please specify:",
            showWhen: { questionId: 'employment_status', equals: ['Other'] },
            group: 'Employment status',
        },
        {
            id: 'marital_status',
            kind: 'single_select',
            label: 'Marital status',
            required: true,
            options: ['Single', 'Co-habiting', 'Married', 'Divorced', 'Widowed'],
            group: 'Marital status',
        },
        {
            id: 'has_financial_dependents',
            kind: 'yes_no',
            label: 'Do you have financial dependents?',
            required: true,
            explainer: 'If yes, please provide the following details:',
            group: 'Dependents',
        },
        {
            id: 'dependent_1_name',
            kind: 'short_text',
            label: 'Dependent 1 — Name',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_1_date_of_birth',
            kind: 'date',
            label: 'Dependent 1 — Date of birth',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_1_relationship',
            kind: 'short_text',
            label: 'Dependent 1 — Relationship to you',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_1_nature',
            kind: 'short_text',
            label: 'Dependent 1 — Nature of dependency',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_2_name',
            kind: 'short_text',
            label: 'Dependent 2 — Name',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_2_date_of_birth',
            kind: 'date',
            label: 'Dependent 2 — Date of birth',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_2_relationship',
            kind: 'short_text',
            label: 'Dependent 2 — Relationship to you',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_2_nature',
            kind: 'short_text',
            label: 'Dependent 2 — Nature of dependency',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_3_name',
            kind: 'short_text',
            label: 'Dependent 3 — Name',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_3_date_of_birth',
            kind: 'date',
            label: 'Dependent 3 — Date of birth',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_3_relationship',
            kind: 'short_text',
            label: 'Dependent 3 — Relationship to you',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_3_nature',
            kind: 'short_text',
            label: 'Dependent 3 — Nature of dependency',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_4_name',
            kind: 'short_text',
            label: 'Dependent 4 — Name',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_4_date_of_birth',
            kind: 'date',
            label: 'Dependent 4 — Date of birth',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_4_relationship',
            kind: 'short_text',
            label: 'Dependent 4 — Relationship to you',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },
        {
            id: 'dependent_4_nature',
            kind: 'short_text',
            label: 'Dependent 4 — Nature of dependency',
            showWhen: { questionId: 'has_financial_dependents', equals: ['Yes'] },
            group: 'Dependents',
        },

        {
            // The two ISA declaration questions (SEP-280). The wording follows
            // section 2 of the firm's ISA declaration form. They show only
            // when the client chose an ISA, and they are required then.
            id: 'isa_initial_subscription',
            kind: 'single_select',
            label: 'What is your initial subscription amount?',
            required: true,
            options: ['Full annual ISA allowance', 'Other amount'],
            showWhen: { questionId: 'account_types', equals: ['ISA'] },
            group: 'ISA declaration',
        },
        {
            id: 'isa_initial_subscription_amount',
            kind: 'currency',
            label: 'Other (please detail amount)',
            required: true,
            showWhen: { questionId: 'isa_initial_subscription', equals: ['Other amount'] },
            group: 'ISA declaration',
        },
        {
            id: 'isa_subscription_source',
            kind: 'single_select',
            label: 'What is the source of the subscription?',
            required: true,
            options: ['Cash', 'Transfer from an existing stocks & shares ISA'],
            optionNotes: {
                'Transfer from an existing stocks & shares ISA':
                    'You will need to complete a separate form that instructs the transfer.',
            },
            showWhen: { questionId: 'account_types', equals: ['ISA'] },
            group: 'ISA declaration',
        },

        {
            id: 'isa_subscribed_this_year',
            kind: 'yes_no',
            label: 'Have you subscribed to your Individual Savings Account (ISA) for this current financial year?',
            explainer:
                "What does this mean? If you have already used some or all of this year's £20,000 ISA allowance elsewhere, that's fine — your investment manager will discuss options with you, such as using next year's allowance or moving existing investments into an ISA over time (sometimes called 'Bed & ISA'). This may have tax implications, and we recommend you take tax advice — Dowgate Wealth is not a tax adviser.",
            group: 'ISA considerations',
        },
        {
            id: 'isa_subscribed_amount',
            kind: 'currency',
            label: 'If yes, please specify the amount you have subscribed',
            showWhen: { questionId: 'isa_subscribed_this_year', equals: ['Yes'] },
            group: 'ISA considerations',
        },
        {
            id: 'isa_regular_contributions_required',
            kind: 'yes_no',
            label: 'Do you plan to pay into your ISA regularly?',
            wordingIn: { '2.0': { label: 'Regular contributions required' } },
            explainer:
                'If you have a remaining ISA allowance, what is your ISA strategy for this current financial year?',
            group: 'ISA considerations',
        },
        {
            // SEP-293.6. Form version 3.0 does not ask it. A submission of
            // version 2.0 keeps the answer.
            id: 'isa_bed_and_isa_planned',
            kind: 'yes_no',
            label: 'Bed & ISA planned',
            retiredIn: '3.0',
            group: 'ISA considerations',
        },
        {
            id: 'isa_other_family_members',
            kind: 'yes_no',
            label: 'Do you want to discuss ISAs for other family members?',
            wordingIn: { '2.0': { label: 'ISA considerations for other family members' } },
            group: 'ISA considerations',
        },
        {
            // SEP-293.6. The words are those of the ticket.
            id: 'isa_other_family_members_context',
            kind: 'long_text',
            label: 'Please provide any additional context.',
            showWhen: { questionId: 'isa_other_family_members', equals: ['Yes'] },
            introducedIn: '3.0',
            group: 'ISA considerations',
        },
        {
            id: 'isa_junior_requirements',
            kind: 'yes_no',
            label: 'Do you want to discuss a Junior ISA for a child?',
            wordingIn: { '2.0': { label: 'Junior ISA requirements' } },
            group: 'ISA considerations',
        },
        {
            // SEP-340 item 11. The same optional box as the family question.
            id: 'isa_junior_requirements_context',
            kind: 'long_text',
            label: 'Please provide any additional context.',
            showWhen: { questionId: 'isa_junior_requirements', equals: ['Yes'] },
            introducedIn: '3.0',
            group: 'ISA considerations',
        },

        // SEP-293.2. A Yes opens the estate planning questions of the fact
        // find, in the words of the fact find, and a No closes them. Each
        // answer fills the question of the fact find with the same meaning.
        {
            id: 'has_will',
            kind: 'yes_no',
            label: FACT_FIND_WILL_WORDING.hasWill,
            explainer: 'if yes, please provide details below:',
            group: 'Estate planning',
        },
        {
            id: 'will_law_firm',
            kind: 'short_text',
            label: FACT_FIND_WILL_WORDING.lawFirm,
            wordingIn: { '2.0': { label: 'Law firm' } },
            showWhen: { questionId: 'has_will', equals: ['Yes'] },
            group: 'Estate planning',
        },
        {
            id: 'will_firm_contact_name',
            kind: 'short_text',
            label: FACT_FIND_WILL_WORDING.firmContactName,
            introducedIn: '3.0',
            showWhen: { questionId: 'has_will', equals: ['Yes'] },
            group: 'Estate planning',
        },
        {
            id: 'will_firm_contact_details',
            kind: 'long_text',
            label: FACT_FIND_WILL_WORDING.firmContactDetails,
            introducedIn: '3.0',
            showWhen: { questionId: 'has_will', equals: ['Yes'] },
            group: 'Estate planning',
        },
        {
            id: 'will_last_review_date',
            kind: 'date',
            label: FACT_FIND_WILL_WORDING.lastReview,
            wordingIn: { '2.0': { label: 'Last review date' } },
            showWhen: { questionId: 'has_will', equals: ['Yes'] },
            group: 'Estate planning',
        },
        {
            id: 'will_executor_contact_details',
            kind: 'long_text',
            label: FACT_FIND_WILL_WORDING.executorsContact,
            wordingIn: { '2.0': { label: 'Executor contact details' } },
            showWhen: { questionId: 'has_will', equals: ['Yes'] },
            group: 'Estate planning',
        },
    ],
};

/**
 * The "personal % ownership" box beside one money question of Part 3
 * (SEP-293.1, question Q4). The Fact Find v3 prints a "% owned" column, and
 * the v6 fact find prints this box on the same rows. The id is the id of the
 * money question with `_ownership`, and the box holds a number.
 */
function ownershipQuestion(valueId: string, group: string): ClientApplicationQuestion {
    return {
        id: `${valueId}_ownership`,
        kind: 'number',
        // The row above holds the long description once. This box needs only
        // its own name, and the journey draws it beside the figure
        // (SEP-340 item 12). It printed the long label before.
        label: 'Personal % ownership',
        suffix: '%',
        besidePrevious: true,
        introducedIn: '3.0',
        group,
    };
}

const FINANCIAL_SITUATION_SECTION: ClientApplicationSection = {
    id: 'financial_situation',
    part: 3,
    title: 'Financial situation',
    stepLabel: 'Finances',
    intro: 'Please include income and assets held anywhere — not just what you plan to invest with us. A complete picture is a regulatory requirement and directly affects what we can responsibly recommend. Estimates are fine where exact figures are hard.',
    questions: [
        { id: 'income_employment', kind: 'currency', label: 'Employment:', group: 'Annual income' },
        {
            id: 'income_investment_outside_dowgate',
            kind: 'currency',
            label: 'Investment income from assets held outside Dowgate Wealth:',
            group: 'Annual income',
        },
        ownershipQuestion('income_investment_outside_dowgate', 'Annual income'),
        {
            // SEP-293.1. The v6 fact find keeps the id of this row and gives
            // it these words, so the application does the same.
            id: 'income_state_pension',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.statePensionIncome,
            wordingIn: { '2.0': { label: 'State pension income:' } },
            group: 'Annual income',
        },
        {
            id: 'income_other_pension',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherPensionIncome,
            wordingIn: { '2.0': { label: 'Other pension income:' } },
            group: 'Annual income',
        },
        {
            id: 'income_all_other',
            kind: 'currency',
            label: 'All other income:',
            group: 'Annual income',
        },
        ownershipQuestion('income_all_other', 'Annual income'),
        {
            id: 'income_all_other_sources',
            kind: 'short_text',
            label: 'All other income source(s):',
            group: 'Annual income',
        },

        {
            id: 'commitment_mortgage_rent',
            kind: 'currency',
            label: 'Mortgage/Rent:',
            group: 'Regular annual financial commitments',
        },
        ownershipQuestion('commitment_mortgage_rent', 'Regular annual financial commitments'),
        {
            id: 'commitment_essential_expenses',
            kind: 'currency',
            label: 'Essential expenses:',
            group: 'Regular annual financial commitments',
        },
        ownershipQuestion('commitment_essential_expenses', 'Regular annual financial commitments'),
        {
            id: 'commitment_school_fees',
            kind: 'currency',
            label: 'School fees:',
            group: 'Regular annual financial commitments',
        },
        {
            id: 'commitment_discretionary_expenses',
            kind: 'currency',
            label: 'Discretionary expenses:',
            group: 'Regular annual financial commitments',
        },
        ownershipQuestion(
            'commitment_discretionary_expenses',
            'Regular annual financial commitments',
        ),
        {
            id: 'commitment_other_significant_expenses',
            kind: 'currency',
            label: 'Other significant expenses:',
            group: 'Regular annual financial commitments',
        },
        {
            id: 'commitment_other_significant_expenses_detail',
            kind: 'short_text',
            label: 'Other significant expenses detail:',
            group: 'Regular annual financial commitments',
        },

        {
            id: 'income_versus_expenditure',
            kind: 'single_select',
            label: 'Which of the following best describes your position?',
            explainer:
                "What does this mean? A simple sense-check in your own words — we'll compare it with the figures above.",
            options: ['Income exceeds expenditure', 'Broadly equal', 'Expenditure exceeds income'],
            group: 'Income vs expenditure',
        },

        // SEP-293.1. The asset rows that the Fact Find v3 lists take the
        // words of the v6 fact find, and a "personal % ownership" box where
        // the v6 form prints one (question Q4).
        {
            id: 'asset_main_property_value',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.mainPropertyValue,
            wordingIn: { '2.0': { label: 'Main property value:' } },
            group: 'Assets',
        },
        ownershipQuestion('asset_main_property_value', 'Assets'),
        {
            id: 'asset_other_property_value',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherPropertyValue,
            wordingIn: { '2.0': { label: 'Other property value:' } },
            group: 'Assets',
        },
        ownershipQuestion('asset_other_property_value', 'Assets'),
        {
            id: 'asset_cash_savings_premium_bonds',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.cashSavingsNsi,
            wordingIn: { '2.0': { label: 'Cash/Savings/Premium Bonds:' } },
            group: 'Assets',
        },
        ownershipQuestion('asset_cash_savings_premium_bonds', 'Assets'),
        {
            id: 'asset_cash_held_within_isas',
            kind: 'currency',
            label: 'of which held within ISAs:',
            group: 'Assets',
        },
        {
            id: 'asset_pension_dowgate_sipp',
            kind: 'currency',
            label: 'Dowgate SIPP:',
            explainer: 'Pension assets:',
            group: 'Assets',
        },
        {
            id: 'asset_pension_other_personal',
            kind: 'currency',
            label: 'Other personal pensions:',
            group: 'Assets',
        },
        {
            id: 'asset_pension_occupational_or_other',
            kind: 'currency',
            label: 'Occupational or other pensions:',
            group: 'Assets',
        },
        {
            // SEP-263.26 and decision D5. This is the ownership question the
            // fact find never asked. The form asks it here, under "Other assets
            // and investments", beside the value of the business interests.
            id: 'owns_or_holds_significant_share_in_business',
            kind: 'yes_no',
            label: 'Do you own, or hold a significant share in, a business?',
            explainer: 'Other assets and investments:',
            group: 'Assets',
        },
        {
            id: 'asset_business_interests',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.businessInterestsValue,
            wordingIn: { '2.0': { label: 'Business interests:' } },
            // SEP-340 item 14. A No closes the business fields. Version 2.0
            // showed them whatever the answer was.
            showWhen: {
                questionId: 'owns_or_holds_significant_share_in_business',
                equals: ['Yes'],
            },
            showWhenIn: { '2.0': {} },
            group: 'Assets',
        },
        {
            ...ownershipQuestion('asset_business_interests', 'Assets'),
            showWhen: {
                questionId: 'owns_or_holds_significant_share_in_business',
                equals: ['Yes'],
            },
        },
        {
            id: 'asset_other_investments_value',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherInvestmentsValue,
            wordingIn: { '2.0': { label: 'Value of other investments:' } },
            group: 'Assets',
        },
        ownershipQuestion('asset_other_investments_value', 'Assets'),
        {
            id: 'asset_other_investments_held_within_isas',
            kind: 'currency',
            label: 'of which held within ISAs:',
            group: 'Assets',
        },
        {
            id: 'asset_other',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherMaterialAssetsValue,
            wordingIn: { '2.0': { label: 'Other assets:' } },
            group: 'Assets',
        },
        ownershipQuestion('asset_other', 'Assets'),
        {
            id: 'asset_other_detail',
            kind: 'short_text',
            label: 'Other assets detail:',
            group: 'Assets',
        },
        {
            // SEP-340 item 14. The business interests row of version 3.0 names
            // EIS and unquoted investments already, so the two questions of
            // version 2.0 are retired.
            id: 'assets_include_eis_vct_or_unquoted',
            kind: 'yes_no',
            label: 'Do your other assets include EIS, VCT or unquoted investments?',
            retiredIn: '3.0',
            group: 'Assets',
        },
        {
            id: 'asset_eis_vct_or_unquoted_value',
            kind: 'currency',
            label: 'If yes, approximate value:',
            showWhen: { questionId: 'assets_include_eis_vct_or_unquoted', equals: ['Yes'] },
            retiredIn: '3.0',
            group: 'Assets',
        },

        {
            id: 'liability_credit_card_debt',
            kind: 'currency',
            label: 'Credit card debt:',
            group: 'Liabilities',
        },
        {
            id: 'liability_mortgage_outstanding',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.mortgageOutstanding,
            wordingIn: { '2.0': { label: 'Mortgage outstanding:' } },
            group: 'Liabilities',
        },
        ownershipQuestion('liability_mortgage_outstanding', 'Liabilities'),
        {
            id: 'liability_anticipated_mortgage_payoff_date',
            kind: 'date',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.mortgagePayoffDate,
            wordingIn: { '2.0': { label: 'Anticipated mortgage pay off date:' } },
            group: 'Liabilities',
        },
        {
            id: 'liability_other_debt',
            kind: 'currency',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherMaterialDebtsAmount,
            wordingIn: { '2.0': { label: 'Other debt:' } },
            group: 'Liabilities',
        },
        ownershipQuestion('liability_other_debt', 'Liabilities'),
        {
            id: 'liability_other_debt_detail',
            kind: 'short_text',
            label: EXISTING_CLIENT_MONEY_ROW_LABELS.otherMaterialDebtsDetail,
            wordingIn: { '2.0': { label: 'Other debt detail:' } },
            group: 'Liabilities',
        },

        {
            id: 'income_tax_band',
            kind: 'single_select',
            label: 'Which income tax band applies to you?',
            options: [
                'Basic rate',
                'Higher rate',
                'Additional rate',
                'Non-taxpayer',
                'Non-UK taxpayer',
            ],
            group: 'Tax',
        },
        {
            id: 'cgt_allowance_available',
            kind: 'yes_no',
            label: 'Is the whole of your annual tax-free Capital Gains Tax allowance available to us in the management of your portfolio?',
            group: 'Tax',
        },
        {
            id: 'cgt_allowance_detail',
            kind: 'long_text',
            label: "If 'No' please provide more details",
            showWhen: { questionId: 'cgt_allowance_available', equals: ['No'] },
            group: 'Tax',
        },
        {
            id: 'capital_losses_to_carry_forward',
            kind: 'yes_no',
            label: 'Do you have any capital losses to carry forward?',
            group: 'Tax',
        },
        {
            id: 'capital_losses_amount',
            kind: 'currency',
            label: "If 'Yes' please detail amount",
            showWhen: { questionId: 'capital_losses_to_carry_forward', equals: ['Yes'] },
            group: 'Tax',
        },
        {
            id: 'gains_within_annual_exemption',
            kind: 'yes_no',
            label: 'Do you want your gains to stay within the annual exemption allowance?',
            explainer:
                "If 'Yes', we will always try to avoid a Capital Gains Tax liability. However, there may be situations when a taxable gain is unavoidable due to circumstances outside of our control, such as a company takeover.",
            group: 'Tax',
        },
    ],
};

/**
 * The seven source types of Part 4, with the form's own prompt for each.
 *
 * Each ticked type opens a Source of Wealth box and a Source of Funds box, so
 * the table builds from one list and the ids cannot drift from the labels.
 */
const SOURCE_TYPES: ReadonlyArray<{ key: string; label: string; prompt: string }> = [
    {
        key: 'employment',
        label: 'Employment',
        prompt: 'Please provide details of your occupation and the nature of the business.',
    },
    {
        key: 'savings_investments',
        label: 'Savings & Investments',
        prompt: 'Please provide details of how these were accumulated.',
    },
    {
        key: 'inheritance',
        label: 'Inheritance',
        prompt: 'Please provide the full name of the deceased, relationship to you and their primary source of wealth.',
    },
    {
        key: 'gift',
        label: 'Gift',
        prompt: 'Please provide the full name of the donor, their relationship to you and their primary source of wealth.',
    },
    {
        key: 'property',
        label: 'Property',
        prompt: 'Please provide details of the property, including its address and date sold if relevant.',
    },
    {
        key: 'business',
        label: 'Business',
        prompt: 'Please provide details of the business including the name of the business, and the nature of its activities.',
    },
    {
        key: 'other',
        label: 'Other',
        prompt: "If your Source of Wealth is 'other', please provide further details.",
    },
];

const SOURCE_OF_WEALTH_AND_FUNDS_SECTION: ClientApplicationSection = {
    id: 'source_of_wealth_and_funds',
    part: 4,
    // SEP-280. The firm named the part "Source of Wealth", in the heading and
    // in the stepper. The section id does not change: a stored draft resumes
    // at it.
    title: 'Source of Wealth',
    stepLabel: 'Source of Wealth',
    intro: "'Source of wealth' is how you built your money over time; 'source of funds' is where the specific money you'll invest is coming from. We need to understand both of these elements for a clearer picture of your financial situation, so we can manage your investments and service your needs in the best way. These requirements are also part of the UK's broader anti-money laundering regulations and help ensure the integrity of the financial system. Specific detail (employer names, dates, sale details) speeds up your application.",
    questions: [
        {
            id: 'source_types',
            kind: 'multi_select',
            label: 'Source Type',
            required: true,
            options: SOURCE_TYPES.map((type) => type.label),
            optionNotes: Object.fromEntries(SOURCE_TYPES.map((type) => [type.label, type.prompt])),
        },
        // Each detail box opens when the client ticks its type, and it is
        // REQUIRED while it is open (SEP-280). A closed box is never required,
        // so the client answers the types they chose and no other.
        ...SOURCE_TYPES.flatMap((type): ClientApplicationQuestion[] => [
            // SEP-340 item 15. The prompt of the type prints once, above the
            // pair, and the type is the heading of the pair. Each box has a
            // short label, and the country is help text.
            {
                id: `source_of_wealth_${type.key}_detail`,
                kind: 'long_text',
                label: 'Source of wealth',
                wordingIn: {
                    '2.0': {
                        label: `${type.label} — Source of Wealth detail (including country where wealth is/was generated)`,
                        explainer: type.prompt,
                    },
                },
                explainer: type.prompt,
                explainerParagraphs: [
                    { text: 'Include the country where the wealth is or was generated.' },
                ],
                required: true,
                showWhen: { questionId: 'source_types', equals: [type.label] },
                group: type.label,
            },
            {
                id: `source_of_funds_${type.key}_detail`,
                kind: 'long_text',
                label: 'Source of funds',
                wordingIn: {
                    '2.0': {
                        label: `${type.label} — Source of Funds detail (including country where funds are/were generated)`,
                        explainer: type.prompt,
                    },
                },
                explainer:
                    'Where is the money you will invest coming from now? For example, which account, or which sale.',
                explainerParagraphs: [
                    { text: 'Include the country where the funds are or were generated.' },
                ],
                required: true,
                showWhen: { questionId: 'source_types', equals: [type.label] },
                group: type.label,
            },
        ]),
    ],
};

/**
 * The frequency choices of Part 5 (SEP-280).
 *
 * The frequency was free text until SEP-280. The firm asked for a closed list,
 * and `Other` opens a text box for the client's own words. An answer saved
 * before SEP-280 holds free text, and `upgradeClientApplicationAnswers` moves
 * it to `Other` and to the text box.
 */
export const CLIENT_APPLICATION_FREQUENCY_OPTIONS = ['Monthly', 'Quarterly', 'Other'] as const;

/**
 * The optional rows of tax residency (SEP-286), in order. Each row opens with
 * the yes or no question `openerId`. `upgradeClientApplicationAnswers` reads
 * `questionIds` to find a row that a draft already fills.
 */
export const CLIENT_APPLICATION_TAX_RESIDENCY_OPTIONAL_ROWS = [
    {
        openerId: 'has_tax_residency_2',
        questionIds: [
            'tax_residency_2_country',
            'tax_residency_2_tax_resident',
            'tax_residency_2_citizen',
            'tax_residency_2_tin',
        ],
    },
    {
        openerId: 'has_tax_residency_3',
        questionIds: [
            'tax_residency_3_country',
            'tax_residency_3_tax_resident',
            'tax_residency_3_citizen',
            'tax_residency_3_tin',
        ],
    },
] as const;

/**
 * The previous address questions (SEP-285). They open when the answer to
 * `lived_at_current_address_3_years` is No. `upgradeClientApplicationAnswers`
 * reads `questionIds` to find a draft that already holds a previous address.
 */
export const CLIENT_APPLICATION_PREVIOUS_ADDRESS_GATE = {
    gateId: 'lived_at_current_address_3_years',
    questionIds: [
        'previous_address_house_no_name',
        'previous_address_street',
        'previous_address_town_city',
        'previous_address_county',
        'previous_address_postcode',
        'previous_address_country',
        'previous_address_tenure',
    ],
} as const;

/** The three frequency questions of Part 5, each with its `Other` text box. */
export const CLIENT_APPLICATION_FREQUENCY_QUESTION_IDS = [
    'regular_withdrawals_frequency',
    'capital_withdrawal_frequency',
    'ongoing_account_funding_frequency',
] as const;

/** The id of the text box that `Other` opens under one frequency question. */
export function clientApplicationFrequencyOtherId(frequencyId: string): string {
    return `${frequencyId}_other`;
}

/** One frequency question and its `Other` text box, open when the parent is Yes. */
function frequencyQuestions(id: string, parentId: string): ClientApplicationQuestion[] {
    return [
        {
            id,
            kind: 'single_select',
            label: 'Frequency',
            options: [...CLIENT_APPLICATION_FREQUENCY_OPTIONS],
            showWhen: { questionId: parentId, equals: ['Yes'] },
        },
        {
            id: clientApplicationFrequencyOtherId(id),
            kind: 'short_text',
            label: 'Other frequency (please specify)',
            required: true,
            showWhen: { questionId: id, equals: ['Other'] },
        },
    ];
}

const ACCOUNT_TYPE_AND_USAGE_SECTION: ClientApplicationSection = {
    id: 'account_type_and_usage',
    part: 5,
    // The account type left this part on 2026-09-22 for the Service type
    // block, so the heading names what the part still asks. The section id is
    // the id a stored draft resumes at, so it does not change.
    title: 'Account usage',
    stepLabel: 'Account usage',
    intro: "It's important for us to understand exactly what sort of account you require and also your expectations regarding funding and withdrawals to and from your portfolio, so we can plan your investments accordingly. It's also a regulatory requirement and additional protection for you if, for example, we observe any unusual activity.",
    questions: [
        // SEP-293.1. The Fact Find v3 asks the main purpose of the account.
        // The client reads the list of the v6 fact find (question Q5).
        {
            id: 'account_usage',
            kind: 'multi_select',
            label: EXISTING_CLIENT_WORDING.accountUsage,
            options: [...EXISTING_CLIENT_ACCOUNT_USAGES],
            introducedIn: '3.0',
        },
        {
            id: 'account_usage_other',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.accountUsageOther,
            showWhen: { questionId: 'account_usage', equals: ['Other'] },
            introducedIn: '3.0',
        },
        {
            id: 'regular_withdrawals_needed',
            kind: 'yes_no',
            label: 'Are regular withdrawals needed?',
            explainer:
                'For example, dividends paid out monthly or quarterly to your personal bank account',
        },
        {
            // SEP-280. The amount is needed, unless the client takes all of
            // the accumulated income, which is an amount they cannot know.
            id: 'regular_withdrawals_amount',
            kind: 'currency',
            label: 'If yes: Amount',
            required: true,
            requiredUnlessTicked: 'regular_withdrawals_all_income',
            showWhen: { questionId: 'regular_withdrawals_needed', equals: ['Yes'] },
        },
        {
            id: 'regular_withdrawals_all_income',
            kind: 'tick',
            label: 'Tick here for all accumulated income',
            showWhen: { questionId: 'regular_withdrawals_needed', equals: ['Yes'] },
        },
        ...frequencyQuestions('regular_withdrawals_frequency', 'regular_withdrawals_needed'),
        // SEP-293.1. The Fact Find v3 asks the portfolio that pays the
        // withdrawals, and whether they meet essential living costs.
        {
            id: 'regular_withdrawals_portfolio',
            kind: 'short_text',
            label: EXISTING_CLIENT_WORDING.regularWithdrawalsPortfolio,
            placeholder: EXISTING_CLIENT_WORDING.regularWithdrawalsPortfolioPlaceholder,
            showWhen: { questionId: 'regular_withdrawals_needed', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'withdrawals_essential',
            kind: 'yes_no',
            label: EXISTING_CLIENT_WORDING.withdrawalsEssential,
            showWhen: { questionId: 'regular_withdrawals_needed', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'withdrawals_essential_detail',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.withdrawalsEssentialDetail,
            showWhen: { questionId: 'withdrawals_essential', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'capital_withdrawal_expected',
            kind: 'yes_no',
            label: 'Is a significant capital withdrawal expected?',
            explainer: 'For example, a property purchase is planned for next year',
        },
        {
            id: 'capital_withdrawal_amount',
            kind: 'currency',
            label: 'If yes: Amount',
            showWhen: { questionId: 'capital_withdrawal_expected', equals: ['Yes'] },
        },
        ...frequencyQuestions('capital_withdrawal_frequency', 'capital_withdrawal_expected'),
        {
            // SEP-293.1. The Fact Find v3 asks the purpose of the withdrawal.
            id: 'capital_withdrawal_purpose',
            kind: 'short_text',
            label: EXISTING_CLIENT_WORDING.capitalWithdrawalPurpose,
            showWhen: { questionId: 'capital_withdrawal_expected', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'ongoing_account_funding_expected',
            kind: 'yes_no',
            label: 'Is ongoing account funding expected?',
            explainer:
                'For example, transfer of funds from an annual bonus to your investment account',
        },
        {
            id: 'ongoing_account_funding_amount',
            kind: 'currency',
            label: 'If yes: Amount',
            showWhen: { questionId: 'ongoing_account_funding_expected', equals: ['Yes'] },
        },
        ...frequencyQuestions(
            'ongoing_account_funding_frequency',
            'ongoing_account_funding_expected',
        ),
        {
            // SEP-293.1. The Fact Find v3 asks the source of the funds.
            id: 'contribution_source_of_funds',
            kind: 'short_text',
            label: EXISTING_CLIENT_WORDING.contributionSourceOfFunds,
            showWhen: { questionId: 'ongoing_account_funding_expected', equals: ['Yes'] },
            introducedIn: '3.0',
        },
    ],
};

const HEALTH_AND_SUPPORT_SECTION: ClientApplicationSection = {
    id: 'health_and_support',
    part: 6,
    title: 'Health & support',
    stepLabel: 'Health & support',
    // The firm settled on 2026-09-21 that Part 6 STAYS in the online form,
    // because Credas verifies the identity of the applicant before the
    // application reaches an investment manager. Every question of this part is
    // special-category data under Article 9, so each one carries `sensitive`.
    intro: 'These questions help us look after you properly — for example, adapting how we communicate or planning around upcoming events. Share only what you’re comfortable sharing; your answers are handled confidentially and never treated as a problem with your application.',
    questions: [
        // SEP-293.1. Form version 3.0 asks the vulnerability questions of the
        // Fact Find v3 with the words of the v6 fact find.
        {
            id: 'general_health',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.physicalHealth,
            wordingIn: { '2.0': { label: 'How is your general state of health:' } },
            options: [...EXISTING_CLIENT_HEALTH_OPTIONS],
            sensitive: true,
        },
        {
            id: 'mental_health',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.mentalHealth,
            options: [...EXISTING_CLIENT_HEALTH_OPTIONS],
            introducedIn: '3.0',
            sensitive: true,
        },
        {
            // One box serves both health questions, as on the v6 fact find.
            // Version 2.0 asked one health question, and opened the box on it.
            id: 'general_health_detail',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.healthDetail,
            wordingIn: {
                '2.0': { label: "If 'Fair' or 'Poor', please provide further details" },
            },
            showWhenRule: HEALTH_DETAIL_NEEDED,
            showWhenIn: {
                '2.0': { showWhen: { questionId: 'general_health', equals: ['Fair', 'Poor'] } },
            },
            sensitive: true,
        },
        {
            id: 'upcoming_operation',
            kind: 'yes_no',
            label: 'Do you have any upcoming operation in the next 12 months, or are you currently recovering from one?',
            sensitive: true,
        },
        {
            id: 'upcoming_operation_detail',
            kind: 'long_text',
            label: "If 'Yes', please provide further detail",
            showWhen: { questionId: 'upcoming_operation', equals: ['Yes'] },
            sensitive: true,
        },
        {
            id: 'other_health_conditions',
            kind: 'yes_no',
            label: 'Do you have any other health conditions that we should be aware of that may impact your financial planning or risk profile?',
            sensitive: true,
        },
        {
            id: 'other_health_conditions_detail',
            kind: 'long_text',
            label: 'If yes, please provide brief details if you are comfortable providing them to us. For example, chronic conditions or disabilities:',
            showWhen: { questionId: 'other_health_conditions', equals: ['Yes'] },
            sensitive: true,
        },
        {
            id: 'major_life_event',
            kind: 'yes_no',
            label: 'Have you recently experienced a major life event (e.g. bereavement, divorce, retirement, inheritance) relevant to this application? (optional)',
            sensitive: true,
        },
        {
            id: 'major_life_event_detail',
            kind: 'long_text',
            label: 'If yes, and you are comfortable doing so, please give brief details',
            showWhen: { questionId: 'major_life_event', equals: ['Yes'] },
            sensitive: true,
        },
        {
            // SEP-293.1. Form version 3.0 asks a yes or no question and a
            // detail box in its place (question Q3). A draft moves its text to
            // the detail box.
            id: 'support_needs',
            kind: 'long_text',
            label: 'Is there anything we can do to support you — for example, how we communicate? (optional)',
            retiredIn: '3.0',
            sensitive: true,
        },
        {
            id: 'communication_needs',
            kind: 'yes_no',
            label: EXISTING_CLIENT_WORDING.communicationNeeds,
            introducedIn: '3.0',
            sensitive: true,
        },
        {
            id: 'communication_needs_detail',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.communicationNeedsDetail,
            showWhen: { questionId: 'communication_needs', equals: ['Yes'] },
            introducedIn: '3.0',
            sensitive: true,
        },
        {
            id: 'trusted_contact',
            kind: 'yes_no',
            label: EXISTING_CLIENT_WORDING.trustedContact,
            explainer: TRUSTED_CONTACT_STATEMENT,
            introducedIn: '3.0',
            sensitive: true,
        },
        {
            id: 'trusted_contact_details',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.trustedContactDetails,
            showWhen: { questionId: 'trusted_contact', equals: ['Yes'] },
            introducedIn: '3.0',
            sensitive: true,
        },
        {
            id: 'lpa_registered',
            kind: 'yes_no',
            label: EXISTING_CLIENT_WORDING.lpaRegistered,
            explainer: EXISTING_CLIENT_WORDING.lpaRegisteredWhy,
            introducedIn: '3.0',
            sensitive: true,
        },
    ],
};

/** The six products of Part 7, with the form's own one-line gloss on each. */
const PRODUCT_EXPERIENCE_ROWS: ReadonlyArray<{ key: string; label: string; gloss: string }> = [
    {
        key: 'cash_deposits',
        label: 'Cash/Deposits',
        gloss: 'savings accounts and cash held for interest.',
    },
    {
        key: 'fixed_income',
        label: 'Fixed Income',
        gloss: 'bonds: loans to governments or companies that pay interest.',
    },
    { key: 'equities', label: 'Equities', gloss: 'shares in individual companies.' },
    {
        key: 'funds',
        label: 'Funds',
        gloss: 'pooled investments holding many assets, such as unit trusts, OEICs and ETFs.',
    },
    {
        key: 'alternatives',
        label: 'Alternatives',
        gloss: 'investments outside shares, bonds and cash — for example property funds, commodities or private assets.',
    },
    {
        key: 'derivatives',
        label: 'Derivatives',
        gloss: 'contracts whose value depends on something else, such as options and futures; typically higher-risk and more complex.',
    },
];

/** The intro of the product grid of Part 7, verbatim. */
const PRODUCT_EXPERIENCE_INTRO =
    "Which of the following have you had experience dealing with — or could you confidently explain to a friend? Tell us how long you've been familiar with each. If you've only heard of something but couldn't say how it works, choose 'None' — that's completely fine.";

const INVESTMENT_OBJECTIVES_AND_EXPERIENCE_SECTION: ClientApplicationSection = {
    id: 'investment_objectives_and_experience',
    part: 7,
    title: 'Investment objectives & experience',
    stepLabel: 'Objectives',
    intro: "It's important for us to understand your objectives and any previous investment experience you have had. Not only is this a UK regulatory requirement. It also helps ensure the portfolio we build for you is suitable, and the advice and way we communicate with you is appropriate.",
    questions: [
        {
            // The three descriptions are the form's own, and they are longer
            // and different from `INVESTMENT_OBJECTIVE_EXPLANATIONS`, which the
            // fact find uses. Both are firm copy, and neither replaces the
            // other.
            //
            // SEP-293.1. The Fact Find v3 adds the option Capital
            // Preservation, so the option set changes. Form version 3.0
            // retires this id and asks `current_investment_objective`.
            id: 'investment_objective',
            kind: 'single_select',
            label: 'What best describes your investment objective?',
            required: true,
            retiredIn: '3.0',
            options: ['Income', 'Growth', 'Balance of income and growth'],
            optionNotes: {
                Income: 'Your primary goal is to receive regular income from your investments, typically through dividends and interest payments. You prioritise investments that can provide a sustainable income stream over those offering capital growth potential. You understand that focusing solely on income generation may result in lower long-term capital growth.',
                Growth: 'You are primarily focused on growing the value of your portfolio over the longer term and are less concerned with generating immediate income. You understand that pursuing growth may involve higher levels of risk and market volatility, and you are comfortable reinvesting any income generated to potentially enhance long-term returns.',
                'Balance of income and growth':
                    'You seek a combination of capital growth and income from your investments. This approach aims to provide some regular income while also growing your capital over time. The split between growth and income can be tailored to your specific needs, but typically involves a broadly equal focus on both objectives.',
            },
        },
        {
            // SEP-293.1. The four objectives of the Fact Find v3, with the
            // words and the notes of the v6 fact find.
            id: 'current_investment_objective',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.investmentObjective,
            required: true,
            options: [...EXISTING_CLIENT_INVESTMENT_OBJECTIVES],
            optionNotes: EXISTING_CLIENT_INVESTMENT_OBJECTIVE_EXPLANATIONS,
            introducedIn: '3.0',
        },
        {
            id: 'investment_restrictions',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.investmentRestrictions,
            wordingIn: {
                '2.0': {
                    label: 'If you have any ethical, environmental, geographic, sector, or stock specific restrictions, please provide these in as much detail as possible below:',
                },
            },
            explainer:
                "What does this mean? Tell us about anything you don't want to hold — for example tobacco or fossil-fuel companies, specific countries, or an individual company (perhaps one you work for). The more specific you are, the more precisely we can follow your wishes. Restrictions can narrow the investments available to us, which may affect performance versus an unrestricted portfolio.",
        },
        {
            id: 'portfolio_duration',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.experienceLength,
            wordingIn: { '2.0': { label: 'How long have you had an investment portfolio?' } },
            explainer:
                "What does this mean? We mean an account where money is invested in markets — with us, another manager, or self-managed. Cash savings accounts don't count; a workplace pension counts only if you've actively chosen its investments.",
            options: ['None', 'under 1 yr', '1-3 yrs', '3-6 yrs', '7-9 yrs', '10+ yrs'],
        },
        {
            id: 'knowledgeable_investor',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.knowledgeableInvestor,
            wordingIn: { '2.0': { label: 'Do you consider yourself a knowledgeable investor?' } },
            explainer:
                'What does this mean? We consider a knowledgeable investor to be someone who understands why markets move the way they do — for example, how interest rates and inflation affect different types of investment. Be honest here: there are no wrong answers, and it simply helps us communicate with you in the right way.',
            options: [
                'Strongly disagree',
                'Disagree',
                'Somewhat disagree',
                'Somewhat agree',
                'Agree',
                'Strongly agree',
            ],
        },
        {
            // SEP-293.1. The Fact Find v3 asks how decisions were made.
            id: 'investment_decisions',
            kind: 'single_select',
            label: EXISTING_CLIENT_WORDING.investmentDecisions,
            options: [...EXISTING_CLIENT_INVESTMENT_DECISIONS],
            introducedIn: '3.0',
        },
        // The intro of the grid prints above the first row, where the paper
        // form prints it: its "choose 'None'" names an option of the grid.
        ...PRODUCT_EXPERIENCE_ROWS.map(
            (row, index): ClientApplicationQuestion => ({
                id: `product_experience_${row.key}`,
                kind: 'single_select',
                label: row.label,
                ...(index === 0
                    ? {
                          explainer: PRODUCT_EXPERIENCE_INTRO,
                          explainerParagraphs: [{ text: row.gloss }],
                      }
                    : { explainer: row.gloss }),
                options: [...PRODUCT_EXPERIENCE_SCALE],
                group: 'Product experience',
            }),
        ),
        // SEP-293.1. The asset class grid of the Fact Find v3: for each class,
        // has the client invested in it, and could the client explain it.
        // The rows of product experience stay, because the score of
        // knowledge and experience reads them (question Q3).
        ...EXISTING_CLIENT_ASSET_CLASSES.flatMap((assetClass): ClientApplicationQuestion[] => [
            {
                id: `asset_invested_${assetClass.key}`,
                kind: 'yes_no',
                label: existingClientAssetInvestedLabel(assetClass),
                explainer: existingClientAssetScope(assetClass),
                introducedIn: '3.0',
                group: 'Product experience',
            },
            {
                id: `asset_explain_${assetClass.key}`,
                kind: 'yes_no',
                label: existingClientAssetExplainLabel(assetClass),
                explainer: existingClientAssetScope(assetClass),
                introducedIn: '3.0',
                group: 'Product experience',
            },
        ]),
        {
            id: 'other_product_experience',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.otherProductsExperience,
            wordingIn: {
                '2.0': {
                    label: 'Do you have experience with any other investment products? If so, please specify and provide the years of investment experience:',
                },
            },
            group: 'Product experience',
        },
        {
            id: 'invested_in_aim',
            kind: 'yes_no',
            label: 'Have you ever invested in AIM before?',
            explainer:
                "What does this mean? AIM is the London Stock Exchange's market for smaller, growing companies. AIM shares can offer higher growth potential, but they are usually harder to buy and sell quickly and carry a higher risk of large losses than shares in bigger companies. We ask because smaller companies can feature in our portfolios, and your familiarity with them helps us assess what's suitable for you.",
        },
        {
            id: 'taken_part_in_placing_or_new_issue',
            kind: 'yes_no',
            label: 'Have you ever taken part in a placing or a new issue (directly or indirectly through a fund or a portfolio)?',
            explainer:
                "What does this mean? A 'placing' or 'new issue' is when a company raises money by selling new shares to investors, often before those shares trade on the open market. Taking part — directly or through a fund — involves particular risks, such as limited information and the possibility the shares fall once trading begins. We ask because it tells us about your experience with this kind of opportunity.",
        },
        {
            id: 'has_financial_qualifications',
            kind: 'yes_no',
            label: EXISTING_CLIENT_WORDING.financialQualifications,
            wordingIn: { '2.0': { label: 'Do you have any financial qualifications?' } },
            explainer:
                "What does this mean? For example accountancy qualifications, or investment qualifications recognised by the FCA (such as CISI, IMC or CFA exams). This contributes to our picture of your investment knowledge — it doesn't change the service you receive.",
        },
        {
            id: 'financial_qualification_types',
            kind: 'multi_select',
            label: 'If yes, please indicate which apply (tick all that apply):',
            options: [
                'Accountancy (ACA/ACCA/CIMA)',
                'FCA-recognised investment qualifications (CISI, IMC, CFA)',
                'Actuarial',
                'Legal-financial',
                'Other',
            ],
            showWhen: { questionId: 'has_financial_qualifications', equals: ['Yes'] },
        },
        {
            id: 'financial_qualification_other',
            kind: 'short_text',
            label: 'Other (please specify)',
            showWhen: { questionId: 'financial_qualification_types', equals: ['Other'] },
        },
        {
            // SEP-293.1. The Fact Find v3 asks the details of the qualification.
            id: 'financial_qualification_detail',
            kind: 'short_text',
            label: EXISTING_CLIENT_WORDING.financialQualificationDetail,
            explainer: EXISTING_CLIENT_WORDING.financialQualificationDetailWhy,
            showWhen: { questionId: 'has_financial_qualifications', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'worked_in_financial_industry',
            kind: 'yes_no',
            label: 'Have you ever worked in the financial industry before?',
            explainer:
                "What does this mean? If yes, we'll ask what kind of role — experience in investment roles tells us something different from, say, operational roles. This helps us judge how much detail and explanation you'd like from us.",
        },
        {
            id: 'financial_industry_capacities',
            kind: 'multi_select',
            label: 'If yes, please indicate the capacity in which you worked (tick all that apply):',
            options: [
                'Investment management',
                'Advisory',
                'Trading',
                'Operations & administration',
                'Compliance & risk',
                'Other',
            ],
            showWhen: { questionId: 'worked_in_financial_industry', equals: ['Yes'] },
        },
        {
            id: 'financial_industry_capacity_other',
            kind: 'short_text',
            label: 'Other (please specify)',
            showWhen: { questionId: 'financial_industry_capacities', equals: ['Other'] },
        },
        {
            id: 'financial_industry_years_in_role',
            kind: 'number',
            label: 'Years in role:',
            showWhen: { questionId: 'worked_in_financial_industry', equals: ['Yes'] },
        },
        {
            // SEP-293.1. The Fact Find v3 asks the details of the role. The
            // question above keeps its words of version 2.0, because the v6
            // words ask only about a role that involves investments, and the
            // capacities above include other roles.
            id: 'worked_in_finance_detail',
            kind: 'long_text',
            label: EXISTING_CLIENT_WORDING.workedInFinanceDetail,
            explainer: EXISTING_CLIENT_WORDING.workedInFinanceDetailWhy,
            showWhen: { questionId: 'worked_in_financial_industry', equals: ['Yes'] },
            introducedIn: '3.0',
        },
        {
            id: 'professional_client_interest',
            kind: 'acknowledgement',
            label: 'I believe I may qualify as a professional client and would like to discuss this with my Investment Manager.',
            // An opt-in, so the client can leave it blank.
            optional: true,
            group: 'Client classification',
            statements: [
                {
                    id: 'client_classification_note_1',
                    text: 'By default we treat you as a retail client. This gives you the highest level of protection under UK financial regulation — including detailed suitability obligations, clearer disclosures, and access to the Financial Ombudsman Service and the Financial Services Compensation Scheme where eligible.',
                },
                {
                    id: 'client_classification_note_2',
                    text: "Some clients with substantial knowledge, experience and resources can ask to be treated as a professional client. If you're re-classified, you lose some of these protections — we're allowed to assume you understand risks with less explanation, some disclosures are reduced, and your access to the Ombudsman and FSCS may be affected.",
                },
                {
                    id: 'client_classification_note_3',
                    text: "Most of our individual clients are retail clients, and being retail does not restrict the quality of service you receive. If you believe you qualify as a professional client and want to discuss it, tick below and your investment manager will contact you — we'll need to assess your experience and gather evidence before any change is made.",
                },
            ],
        },
    ],
};

/**
 * The v6 words of one question of the risk instrument (SEP-293.1). Form
 * version 3.0 asks each risk question with these words.
 */
function riskPrompt(atrQuestionId: string): string {
    const prompt = EXISTING_CLIENT_ATR_WORDING[atrQuestionId]?.prompt;
    if (prompt === undefined) throw new Error(`the v6 form has no words for ${atrQuestionId}`);
    return prompt;
}

/**
 * The options of one risk question in the v6 words, in the order of
 * `options`. An option that the v6 form does not reword keeps its words.
 */
function riskOptions(atrQuestionId: string, options: readonly string[]): string[] {
    const reworded = EXISTING_CLIENT_ATR_WORDING[atrQuestionId]?.options ?? {};
    return options.map((option) => reworded[option] ?? option);
}

/** The options of one risk question that form version 3.0 asks under a new id. */
function changedRiskOptions(questionId: string): string[] {
    const change = clientApplicationRiskQuestionChange(questionId);
    if (change === undefined || change.canonicalOptions === null) {
        throw new Error(`${questionId} has no canonical options`);
    }
    return clientApplicationRiskOptions(change.atrQuestionId, change.canonicalOptions);
}

const RISK_TOLERANCE_SECTION: ClientApplicationSection = {
    id: 'risk_tolerance_and_capacity_for_loss',
    part: 8,
    title: 'Risk tolerance & capacity for loss',
    stepLabel: 'Risk',
    intro: "Whether it's your first investment or you're an experienced investor, grasping the relationship between risk and return is fundamental to making informed decisions about your financial future. The questions in this section help us evaluate your tolerance and capacity for risk, ensuring we apply the appropriate risk profile to your portfolio.",
    questions: [
        {
            id: 'risk_protection_over_returns',
            kind: 'single_select',
            label: riskPrompt('atr_q1'),
            wordingIn: {
                '2.0': {
                    label: 'With regards to this investment, I generally consider protecting my investment is more important to me than the potential to achieve high returns:',
                },
            },
            explainer:
                'Why we ask: This tells us where you sit on the fundamental trade-off: higher potential returns always come with a higher chance of losses.',
            options: [...AGREEMENT_SCALE],
        },
        {
            id: 'risk_prefer_fewer_ups_and_downs',
            kind: 'single_select',
            label: riskPrompt('atr_q2'),
            wordingIn: {
                '2.0': {
                    label: 'With regards to this investment, I would prefer fewer ups and downs in value, and I would be willing to accept the lower returns this investment may make:',
                },
            },
            explainer:
                'Why we ask: This gauges how you feel about volatility — the size of the rises and falls your portfolio may experience along the way, separate from where it ends up.',
            options: [...AGREEMENT_SCALE],
        },
        {
            id: 'risk_comfortable_with_smaller_companies',
            kind: 'single_select',
            label: riskPrompt('atr_q3'),
            wordingIn: {
                '2.0': {
                    label: 'Smaller companies can achieve faster and higher growth but can also be illiquid and at greater risk of failure than FTSE 100 companies. I would be comfortable to invest in this type of company for the potential to achieve a larger return:',
                },
            },
            explainer:
                'Why we ask: Smaller companies can grow faster than large ones, but their shares can be harder to sell and individual companies are more likely to fail. This asks whether that trade-off appeals to you.',
            options: [...AGREEMENT_SCALE],
        },
        {
            id: 'risk_understands_smaller_company_risks',
            kind: 'single_select',
            retiredIn: '3.0',
            label: 'I understand – and am comfortable with – the risks associated with investing in smaller companies:',
            explainer:
                "Why we ask: This is about understanding rather than appetite. If you're unsure what those risks are, our Risk & Investment Guide explains them.",
            options: [...AGREEMENT_SCALE],
        },
        {
            // SEP-293.1. The v6 question asks about comfort through the
            // volatility of smaller companies, not about an understanding of
            // their risks, so it has its own id.
            id: 'risk_comfortable_through_smaller_company_volatility',
            kind: 'single_select',
            label: riskPrompt('atr_q4'),
            options: [...AGREEMENT_SCALE],
            introducedIn: '3.0',
        },
        {
            id: 'risk_response_to_quarter_fall',
            kind: 'single_select',
            label: riskPrompt('atr_q5'),
            wordingIn: {
                '2.0': {
                    label: 'In the four weeks before the first lockdown (23 March 2020), the FTSE 100 fell 25% in value. If I owned an investment that lost over 25% in four weeks, I would:',
                },
            },
            explainer:
                "Why we ask: There's no right answer. How people actually behave in sharp falls matters more to long-term outcomes than almost anything else, so we ask you to imagine it honestly.",
            options: [
                'Sell all the remaining investment',
                'Sell some of the remaining investment',
                'Hold onto investment and sell nothing',
                'Buy more of the investment',
            ],
        },
        {
            id: 'risk_loss_that_prompts_move_to_cash',
            kind: 'single_select',
            retiredIn: '3.0',
            label: 'Over the short term, investments are likely to fall as well as rise. I would begin to feel the need to cut my losses and move to cash upon a loss of:',
            explainer:
                "Why we ask: This helps us understand at what point falls would cause you real concern — so the risk level we apply doesn't take you past it in normal market conditions.",
            options: [
                'Any fall is cause for concern',
                'Loss of 15%',
                'Loss of 16-30%',
                'Loss of 31-49%',
                'Loss of 50%+',
            ],
        },
        {
            // SEP-293.1. The v6 question asks about comfort below the
            // original investment. The prefill map gives the instrument the
            // canonical option at the same position (question Q6).
            id: 'risk_comfort_below_original_investment',
            kind: 'single_select',
            label: riskPrompt('atr_q6'),
            explainer:
                "Why we ask: This helps us understand at what point falls would cause you real concern — so the risk level we apply doesn't take you past it in normal market conditions.",
            options: changedRiskOptions('risk_comfort_below_original_investment'),
            introducedIn: '3.0',
        },
        {
            id: 'risk_investment_horizon',
            kind: 'single_select',
            retiredIn: '3.0',
            label: 'How long do you think you will remain invested before taking your money out?',
            explainer:
                'Why we ask: The longer you can stay invested, the more short-term movement a portfolio can ride out. If you expect to need the money within a couple of years, that changes what’s suitable.',
            options: ['0-1 years', '1-3 years', '3-5 years', '5-7 years', '7+ years'],
        },
        {
            // SEP-293.1. The bands of the Fact Find v3 and the v6 fact find.
            id: 'risk_time_horizon',
            kind: 'single_select',
            label: riskPrompt('atr_q7'),
            explainer:
                'Why we ask: The longer you can stay invested, the more short-term movement a portfolio can ride out. If you expect to need the money within a couple of years, that changes what’s suitable.',
            options: changedRiskOptions('risk_time_horizon'),
            introducedIn: '3.0',
        },
        {
            id: 'risk_income_security',
            kind: 'single_select',
            label: riskPrompt('atr_q8'),
            wordingIn: {
                '2.0': {
                    label: 'How secure are your sources of income?',
                    options: ['Very unsecure', 'Unsecure', 'Not sure', 'Secure', 'Very secure'],
                },
            },
            explainer:
                'Why we ask: If your income is secure and your commitments stable, you can generally afford more investment risk than if either might change. We look at these answers — and the two questions that follow — together with your income and outgoings.',
            options: riskOptions('atr_q8', [
                'Very unsecure',
                'Unsecure',
                'Not sure',
                'Secure',
                'Very secure',
            ]),
        },
        {
            id: 'risk_income_change_over_ten_years',
            kind: 'single_select',
            label: riskPrompt('atr_q9'),
            wordingIn: {
                '2.0': {
                    label: 'Over the course of the next ten years, how do you expect your level of income to change, if at all?',
                    options: [...TEN_YEAR_CHANGE_SCALE],
                },
            },
            options: riskOptions('atr_q9', TEN_YEAR_CHANGE_SCALE),
        },
        {
            id: 'risk_liquid_funds_months_of_expenses',
            kind: 'single_select',
            label: riskPrompt('atr_q11'),
            wordingIn: {
                '2.0': { label: 'How many months of expenses could your liquid funds cover?' },
            },
            explainer:
                "Why we ask: Money you could reach quickly, without selling investments — typically cash savings. Having a cash buffer means you're less likely to be forced to sell investments at a bad time.",
            options: [
                'No emergency funds',
                'Less than a month',
                '1-6 months',
                '6-18 months',
                'Over 18 months',
            ],
        },
        {
            id: 'risk_capacity_for_loss',
            kind: 'single_select',
            retiredIn: '3.0',
            label: 'How much of your investments with Dowgate Wealth do you think that you are able to lose without having a significant impact on your future standard of living?',
            explainer:
                "Why we ask: This is about capacity rather than feelings: as a matter of fact, how much of this investment could fall in value before your standard of living was affected? We compare this with the financial details you've given us.",
            options: [
                'No losses (0-10%)',
                'Small losses (11-20%)',
                'Moderate losses (21-49%)',
                'Large losses (50-90%)',
                'Total losses (91-100%)',
            ],
        },
        {
            // SEP-293.1. The bands of the Fact Find v3 and the v6 fact find.
            id: 'risk_bearable_loss',
            kind: 'single_select',
            label: riskPrompt('atr_q12'),
            explainer:
                "Why we ask: This is about capacity rather than feelings: as a matter of fact, how much of this investment could fall in value before your standard of living was affected? We compare this with the financial details you've given us.",
            options: changedRiskOptions('risk_bearable_loss'),
            introducedIn: '3.0',
        },
        {
            id: 'risk_liabilities_change_over_ten_years',
            kind: 'single_select',
            label: riskPrompt('atr_q10'),
            wordingIn: {
                '2.0': {
                    label: 'Over the course of the next ten years, how do you expect your liabilities to change, if at all?',
                    options: [...TEN_YEAR_CHANGE_SCALE],
                },
            },
            options: riskOptions('atr_q10', TEN_YEAR_CHANGE_SCALE),
        },
        {
            // The intended risk profile is a PREFERENCE and never a decision.
            // ADR-0005 and `risk-profile.ts` hold the rule: this answer must
            // never seed the risk-profile recommendation of the Internal
            // Review.
            id: 'intended_risk_profile',
            kind: 'single_select',
            label: 'Your intended risk profile (optional)',
            explainer:
                'This is your preference, not a decision — your investment manager will assess all your answers together and agree the final risk profile with you. Read the points under each risk profile in the list. Choose one profile only, if one reflects the level of risk you have in mind. Or choose the last option to discuss it with your Investment Manager.',
            options: [...INTENDED_RISK_PROFILE_OPTIONS],
            optionNotes: INTENDED_RISK_PROFILE_NOTES,
            group: 'Your intended risk profile (optional)',
        },
    ],
};

const BANK_ACCOUNT_INFORMATION_SECTION: ClientApplicationSection = {
    id: 'bank_account_information',
    part: 9,
    title: 'Bank account information',
    stepLabel: 'Bank account',
    intro: 'For your protection, we only pay money to — and accept money from — this one nominated account in your own name. After you submit this form, we will ask you for a certified copy of a bank statement to verify it. For non-UK bank accounts, please provide IBAN, BIC / ABA numbers and the account currency.',
    questions: [
        { id: 'bank_name', kind: 'short_text', label: 'Bank Name', required: true },
        { id: 'bank_account_name', kind: 'short_text', label: 'Account Name', required: true },
        { id: 'bank_sort_code', kind: 'short_text', label: 'Sort Code', textFormat: 'sort_code' },
        {
            id: 'bank_account_number',
            kind: 'short_text',
            label: 'Account Number',
            textFormat: 'account_number',
        },
        {
            id: 'bank_iban',
            kind: 'short_text',
            label: 'IBAN Number (if relevant)',
            textFormat: 'iban',
        },
        {
            id: 'bank_bic_aba',
            kind: 'short_text',
            label: 'BIC / ABA Number (if relevant)',
            textFormat: 'bic',
        },
        {
            id: 'bank_account_currency',
            kind: 'short_text',
            label: 'Account Currency (if relevant)',
        },
    ],
};

/**
 * The question that opens the third party of Part 10. A client with no third
 * party answers No, so the form needs no blank answer for a person that does
 * not exist. The upgrade step "third party opener" answers Yes for a map that
 * holds a third party already.
 */
export const CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID = 'has_third_party';

const THIRD_PARTY_OPENER: ClientApplicationQuestion = {
    id: CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID,
    kind: 'yes_no',
    label: 'Do you want us to send information about the account to a third party?',
};

/** A question of the third party, shown when the client names a third party. */
function underThirdPartyOpener(question: ClientApplicationQuestion): ClientApplicationQuestion {
    const opener = { questionId: CLIENT_APPLICATION_THIRD_PARTY_OPENER_ID, equals: ['Yes'] };
    const own =
        question.showWhen === undefined
            ? []
            : isConditionList(question.showWhen)
              ? question.showWhen
              : [question.showWhen];
    return { ...question, showWhen: own.length === 0 ? opener : [opener, ...own] };
}

/** The ids of the questions of the third party, the opener excluded. */
export const CLIENT_APPLICATION_THIRD_PARTY_QUESTION_IDS: readonly string[] = [
    'third_party_title',
    'third_party_title_other',
    'third_party_surname',
    'third_party_forenames',
    'third_party_company_name',
    'third_party_address',
    'third_party_postcode',
    'third_party_email',
    'third_party_telephone',
    'third_party_role',
    'third_party_role_other',
    'third_party_correspondence',
    'third_party_correspondence_other',
];

const THIRD_PARTY_DISCLOSURE_SECTION: ClientApplicationSection = {
    id: 'third_party_disclosure',
    part: 10,
    title: 'Third party disclosure',
    stepLabel: 'Third party',
    intro: 'In certain situations, it is useful to have copies of certain reports and documentation relating to your account(s) sent to third parties — for example a tax advisor for assessment and tax filing purposes, or a lawyer for estate planning. This is not for them to make investment decisions, but for information only — a third party listed here cannot give us instructions or make decisions on your account. If you want Dowgate Wealth to provide information regarding the account to a third-party, please provide the relevant details below.',
    questions: [
        THIRD_PARTY_OPENER,
        ...(
            [
                {
                    id: 'third_party_title',
                    kind: 'single_select',
                    label: 'Title',
                    options: [...TITLE_OPTIONS],
                },
                {
                    id: 'third_party_title_other',
                    kind: 'short_text',
                    label: 'Other (please specify)',
                    showWhen: { questionId: 'third_party_title', equals: ['Other'] },
                },
                { id: 'third_party_surname', kind: 'short_text', label: 'Surname' },
                { id: 'third_party_forenames', kind: 'short_text', label: 'Forename(s)' },
                {
                    id: 'third_party_company_name',
                    kind: 'short_text',
                    label: 'Company name (if relevant)',
                },
                { id: 'third_party_address', kind: 'long_text', label: 'Address' },
                {
                    id: 'third_party_postcode',
                    kind: 'short_text',
                    label: 'Postcode',
                    textFormat: 'postcode',
                },
                {
                    id: 'third_party_email',
                    kind: 'short_text',
                    label: 'Email address',
                    textFormat: 'email',
                },
                {
                    id: 'third_party_telephone',
                    kind: 'short_text',
                    label: 'Telephone number',
                    textFormat: 'phone',
                },
                {
                    id: 'third_party_role',
                    kind: 'single_select',
                    label: 'Please detail the role of the third party:',
                    options: ['Lawyer', 'IFA', 'Accountant', 'Trustee', 'Other'],
                },
                {
                    id: 'third_party_role_other',
                    kind: 'short_text',
                    label: 'Other (please specify)',
                    showWhen: { questionId: 'third_party_role', equals: ['Other'] },
                },
                {
                    id: 'third_party_correspondence',
                    kind: 'single_select',
                    label: 'Please detail what correspondence you wish to be provided to the third party:',
                    options: ['None', 'Reporting', 'Online Access', 'Other'],
                },
                {
                    id: 'third_party_correspondence_other',
                    kind: 'short_text',
                    label: 'Other (please specify)',
                    showWhen: { questionId: 'third_party_correspondence', equals: ['Other'] },
                },
            ] satisfies ClientApplicationQuestion[]
        ).map(underThirdPartyOpener),
        // SEP-293.8: the nominated representative, at the end of Part 10. A
        // yes or no question opens it, and the service type selects the
        // variant of the paper form
        // (`client-application-nominated-representative.ts`).
        ...NOMINATED_REPRESENTATIVE_QUESTIONS,
    ],
};

/**
 * Part 11 — the portal security questions.
 *
 * THEY ARE ORDINARY ANSWERS AND THEY ARE NOT CREDENTIALS. The firm settled on
 * 2026-09-21 that the email address and the password are the portal identity,
 * and that these four answers "remain in the form for the client to fill out".
 * They are bank-grade identifiers, so every question carries `sensitive`,
 * `excludeFromPrefill` and `excludeFromStaffReview`. A later slice reads those
 * flags and keeps the answers out of the fact-find prefill map and out of the
 * staff review projection.
 *
 * The form prints seven rows and asks the client to answer four. The form rule
 * `CLIENT_APPLICATION_PORTAL_SECURITY_RULE` counts the answers against
 * `CLIENT_APPLICATION_PORTAL_SECURITY_ANSWERS_REQUIRED`.
 */
const ONLINE_PORTAL_SECURITY_SECTION: ClientApplicationSection = {
    id: 'online_portal_security_questions',
    part: 11,
    title: 'Online portal security questions',
    stepLabel: 'Portal security',
    intro: "Our online portal gives you access to your investment portfolio, so you can see your holdings, the value of your investments and a history of all your transactions. For security, we ask the main account holder to provide answers to the following questions. Choose four questions and give answers you'll remember exactly — we'll use these to verify your identity for the online portal. Answers aren't case-sensitive. Additional access can be provided upon request for joint account holders or third parties.",
    questions: PORTAL_SECURITY_ROWS.map(
        (row): ClientApplicationQuestion => ({
            id: `portal_security_answer_${row.key}`,
            kind: 'short_text',
            label: row.label,
            sensitive: true,
            excludeFromPrefill: true,
            excludeFromStaffReview: true,
            // The client answers four of the seven rows, so a row alone is
            // optional. A form rule counts the answers.
            optional: true,
        }),
    ),
};

const DECLARATIONS_SECTION: ClientApplicationSection = {
    id: 'declarations',
    part: 12,
    title: 'Declarations',
    stepLabel: 'Declarations',
    // Part 13 of the paper form is a wet signature, and it is out of scope. The
    // digital journey replaces it with the submission act, which carries a
    // date, an instant and an actor. The form's own line, "By signing under
    // Part 13 (Signatures), the following declarations apply", therefore has no
    // digital counterpart and the tick below carries the acceptance.
    notes: [
        {
            id: 'data_protection_note',
            text: 'Your data will be processed in line with the Dowgate Wealth Privacy Policy which can be found at: https://dowgatewealth.co.uk/privacy-policy/',
        },
    ],
    questions: [
        {
            id: 'declarations_accepted',
            kind: 'acknowledgement',
            label: 'I accept the declarations above',
            required: true,
            statements: [
                {
                    id: 'declaration_1',
                    text: 'I/We have read and understood the nature and the risk of the product(s) that I/we intend to invest in through this account.',
                },
                {
                    id: 'declaration_2',
                    text: 'I/We act in my/our name(s) as specified on this application form and not on behalf of another party in respect of all matters related to this account. Accordingly, all funds to be deposited and transacted on the account are my/our own funds.',
                },
                {
                    id: 'declaration_3',
                    text: 'I/We am/are not aware of any health or other conditions that I/we may have, other than those I/we have disclosed in this application, that might affect the proper operation of the account.',
                },
                {
                    id: 'declaration_4',
                    text: 'I/We have provided true, accurate and complete information and undertake to update Dowgate Wealth of any changes to the information provided without delay.',
                },
                {
                    id: 'declaration_5',
                    text: 'I/We accept that in certain circumstances Dowgate Wealth will be obliged to share information with UK tax authorities, who may pass it on to other tax authorities.',
                },
                {
                    id: 'declaration_6',
                    text: 'I/We have received, read and understood the following:',
                },
                {
                    id: 'declaration_6a',
                    text: 'a. Dowgate Wealth Terms of Business – including the product risks disclosure (website link: https://dowgatewealth.co.uk/terms-of-business/)',
                },
                {
                    id: 'declaration_6b',
                    text: 'b. Conflict of Interest Policy (website link: https://dowgatewealth.co.uk/conflicts-of-interest-policy/)',
                },
                // SEP-263.39 (ticket section 12). The supplied form prints
                // "c. Schedule of Charges" here. THE LINE IS REMOVED ON
                // PURPOSE: the firm provides the Schedule of Charges at Stage 5,
                // Signing, with the Investment Mandate (SEP-263.37, SEP-318
                // decision 9), so a client cannot declare that they have read
                // it at the moment they apply. 6a, 6b and 6d stay, and the
                // letters keep the form's own lettering, so a reader of the
                // paper form and a reader of the journey see the same items.
                {
                    id: 'declaration_6d',
                    text: 'd. I/We have obtained the Dowgate Wealth Order Execution Policy (website link: https://dowgatewealth.co.uk/dowgate-wealth-ltd-order-execution-policy/)',
                },
                {
                    id: 'declaration_7',
                    text: 'I/We consent to the Order Execution Policy and for any orders to be executed outside a regulated market or a multilateral trading facility.',
                },
                {
                    id: 'declaration_8',
                    text: 'I/We consent for any unexecuted limit orders not to be made public.',
                },
                {
                    id: 'declaration_9',
                    text: 'I/We accept and agree to be bound by the terms provided above and consent to such terms and information including future updates to these be provided to me/us by way of posting on the websites indicated above.',
                },
            ],
        },
        {
            id: 'electronic_communications_consent',
            kind: 'acknowledgement',
            label: 'I/We Accept (please tick this box):',
            required: true,
            group: 'Express consent for receiving information electronically',
            explainer:
                'We are required by regulation to obtain your express consent to provide you with information in an electronic, durable medium that is not paper. For example, by email. If you do not consent to us providing you with information electronically, we will be unable to offer you an account. For the avoidance of doubt, by consenting to receiving information via electronic means, you remain entitled to provide us with specific requests for information via a reasonable alternative form of durable medium.',
            statements: [
                {
                    id: 'electronic_consent_1',
                    text: 'I/We hereby consent to receive information from Dowgate Wealth via email, PDF document or by any other durable medium that is not paper',
                },
                {
                    id: 'electronic_consent_2',
                    text: 'I/We hereby consent to receive information not personally addressed to me/us via the websites that you provide me/us notice of',
                },
            ],
        },
        {
            id: 'marketing_company_news_and_events',
            kind: 'acknowledgement',
            label: 'Company news and events',
            // An opt-in, so the client can leave it blank.
            optional: true,
            group: 'Marketing communication',
            explainer:
                'We offer company news and market commentary which is emailed to the contact email address detailed in Section 1. If you would like to receive this, please tick this box:',
        },
        {
            id: 'marketing_market_commentary_and_insights',
            kind: 'acknowledgement',
            label: 'Market commentary and insights',
            // An opt-in, so the client can leave it blank.
            optional: true,
            group: 'Marketing communication',
        },
    ],
};

// ---------------------------------------------------------------------------
// The bank
// ---------------------------------------------------------------------------

/**
 * The bank of the form: each question of each version, and the rules.
 *
 * The bank holds each question that a version of the form ever held. A
 * question states the versions that hold it, so a reader takes the view of
 * one version with {@link clientApplicationSections}. A test gives its own
 * bank to a reader through `bank` in {@link ClientApplicationFormContext}.
 * Each other caller uses {@link CLIENT_APPLICATION_BANK}.
 */
export interface ClientApplicationBank {
    /**
     * The version line of the form that holds the bank, from the oldest to
     * the newest (SEP-293). Each form has its own line. Absent means the line
     * of the individual form, {@link CLIENT_APPLICATION_FORM_VERSIONS}.
     */
    readonly versions?: readonly string[];
    readonly sections: readonly ClientApplicationSection[];
    /** The computed rules that a `showWhenRule` names, by name. */
    readonly visibilityRules: Readonly<
        Record<ClientApplicationRuleId, ClientApplicationVisibilityRule>
    >;
    /** The rules that read more than one answer. */
    readonly formRules: readonly ClientApplicationFormRule[];
    /**
     * Each question of the newest version of the bank needs an answer,
     * unless it is {@link ClientApplicationQuestion.optional} or a tick. An
     * older version keeps its own rules. An investment
     * manager must get a form with no blank field. A question that not every
     * client can answer carries {@link ClientApplicationQuestion.notApplicable}.
     * A test bank leaves this flag out, and gives `required` by hand.
     */
    readonly requireEveryAnswer?: true;
}

/**
 * What a reader of the bank can give.
 *
 * Each field is optional. The defaults are the current version, no date and
 * the bank of the firm.
 */
export interface ClientApplicationFormContext {
    /**
     * The version to read, in the version line of the form. The default is
     * the current version of the form: `form.currentVersion`, else
     * {@link CLIENT_APPLICATION_FORM_VERSION}.
     */
    readonly version?: string;
    /**
     * The date of today, `YYYY-MM-DD` ({@link clientApplicationCalendarDate}).
     * A draft gives the date of `clockNow()`, and a snapshot gives the date of
     * submission. The default is null.
     */
    readonly today?: string | null;
    /** The bank to read. Only a test gives its own bank. */
    readonly bank?: ClientApplicationBank;
    /**
     * The form to read (SEP-293). Its bank and its current version replace
     * the defaults. Absent means the individual form.
     */
    readonly form?: ClientApplicationFormDefinition;
}

/** The bank that a context names: the bank of its form, its own bank, or the bank of the firm. */
export function clientApplicationContextBank(
    context: ClientApplicationFormContext = {},
): ClientApplicationBank {
    return context.form?.bank ?? context.bank ?? CLIENT_APPLICATION_BANK;
}

/** The version that a context names: its version, or the current version of its form. */
export function clientApplicationContextVersion(
    context: ClientApplicationFormContext = {},
): string {
    return context.version ?? context.form?.currentVersion ?? CLIENT_APPLICATION_FORM_VERSION;
}

/** The version line of one bank, from the oldest to the newest. */
export function clientApplicationBankVersions(bank: ClientApplicationBank): readonly string[] {
    return bank.versions ?? CLIENT_APPLICATION_FORM_VERSIONS;
}

/**
 * The sections of each version, in the order the form prints them.
 *
 * The Service Type block comes first, because the form prints it on page 2
 * above Part 1, and then Parts 1 to 12. Each question of each version is
 * here. {@link CLIENT_APPLICATION_SECTIONS} is the view of the current
 * version.
 */
const BANK_SECTIONS: readonly ClientApplicationSection[] = [
    SERVICE_TYPE_SECTION,
    PERSONAL_INFORMATION_SECTION,
    PERSONAL_CIRCUMSTANCES_SECTION,
    FINANCIAL_SITUATION_SECTION,
    SOURCE_OF_WEALTH_AND_FUNDS_SECTION,
    ACCOUNT_TYPE_AND_USAGE_SECTION,
    HEALTH_AND_SUPPORT_SECTION,
    INVESTMENT_OBJECTIVES_AND_EXPERIENCE_SECTION,
    RISK_TOLERANCE_SECTION,
    BANK_ACCOUNT_INFORMATION_SECTION,
    THIRD_PARTY_DISCLOSURE_SECTION,
    ONLINE_PORTAL_SECURITY_SECTION,
    DECLARATIONS_SECTION,
];

/** The bank of the firm. */
export const CLIENT_APPLICATION_BANK: ClientApplicationBank = {
    sections: BANK_SECTIONS,
    visibilityRules: CLIENT_APPLICATION_VISIBILITY_RULES,
    formRules: CLIENT_APPLICATION_FORM_RULES,
    requireEveryAnswer: true,
};

/** The view of one version: its sections, and its questions by id. */
interface VersionView {
    readonly sections: readonly ClientApplicationSection[];
    readonly byId: ReadonlyMap<string, ClientApplicationQuestion>;
}

const VERSION_VIEWS = new WeakMap<ClientApplicationBank, Map<string, VersionView>>();

/**
 * Whether one version of the form holds the question. `versions` is the
 * version line of the form, and the default is the line of the individual
 * form.
 */
export function isClientApplicationQuestionInVersion(
    question: ClientApplicationQuestion,
    version: string,
    versions: readonly string[] = CLIENT_APPLICATION_FORM_VERSIONS,
): boolean {
    return isInClientApplicationFormVersionSpan(question, version, versions);
}

/** The question as one version of the form holds it: its words and its condition. */
function questionInVersion(
    question: ClientApplicationQuestion,
    version: string,
): ClientApplicationQuestion {
    const wording = question.wordingIn?.[version];
    const visibility = question.showWhenIn?.[version];
    if (wording === undefined && visibility === undefined) return question;
    const resolved: { -readonly [K in keyof ClientApplicationQuestion]?: unknown } = {
        ...question,
    };
    if (wording !== undefined) {
        for (const [key, value] of Object.entries(wording)) {
            if (value !== undefined) resolved[key as keyof ClientApplicationWording] = value;
        }
    }
    if (visibility !== undefined) {
        delete resolved.showWhen;
        delete resolved.showWhenRule;
        if (visibility.showWhen !== undefined) resolved.showWhen = visibility.showWhen;
        if (visibility.showWhenRule !== undefined) resolved.showWhenRule = visibility.showWhenRule;
        if (visibility.required === false) delete resolved.required;
        if (visibility.required === true) resolved.required = true;
    }
    return resolved as ClientApplicationQuestion;
}

/** The label of the tick that completes a question with no answer for the client. */
export const CLIENT_APPLICATION_NOT_APPLICABLE_LABEL = 'Not applicable';

/** The id of the "Not applicable" tick of one question. */
export function clientApplicationNotApplicableId(questionId: string): string {
    return `${questionId}_not_applicable`;
}

/** The kinds that a bank that requires every answer leaves optional. */
const NEVER_REQUIRED_KINDS: ReadonlySet<ClientApplicationQuestionKind> = new Set(['tick']);

/**
 * The question as the version view gives it to the readers: required when the
 * bank requires every answer, and followed by its "Not applicable" tick when
 * it has one.
 *
 * The tick shows when the question shows, and the journey draws it under the
 * field of its question. It waives
 * the question in {@link isClientApplicationQuestionRequired}, as the tick of
 * SEP-280 waives the regular withdrawal amount.
 */
function completedQuestion(
    question: ClientApplicationQuestion,
    bank: ClientApplicationBank,
    version: string,
): readonly ClientApplicationQuestion[] {
    if (!requiresEveryAnswer(bank, version)) return [question];
    const required =
        question.required !== true &&
        question.optional !== true &&
        !NEVER_REQUIRED_KINDS.has(question.kind)
            ? { ...question, required: true as const }
            : question;
    if (!takesNotApplicable(question)) return [required];
    if (question.requiredUnlessTicked !== undefined) {
        throw new Error(`"${question.id}" has a waiver already, so it cannot be "Not applicable"`);
    }
    const tickId = clientApplicationNotApplicableId(question.id);
    const tick: ClientApplicationQuestion = {
        id: tickId,
        kind: 'tick',
        label: CLIENT_APPLICATION_NOT_APPLICABLE_LABEL,
        notApplicableFor: question.id,
        ...(question.group !== undefined ? { group: question.group } : {}),
        ...(question.showWhen !== undefined ? { showWhen: question.showWhen } : {}),
        ...(question.showWhenRule !== undefined ? { showWhenRule: question.showWhenRule } : {}),
        ...(question.excludeFromStaffReview !== undefined
            ? { excludeFromStaffReview: question.excludeFromStaffReview }
            : {}),
        ...(question.sensitive !== undefined ? { sensitive: question.sensitive } : {}),
        ...(question.excludeFromPrefill !== undefined
            ? { excludeFromPrefill: question.excludeFromPrefill }
            : {}),
    };
    return [{ ...required, required: true, notApplicable: true }, tick];
}

/**
 * Whether one version of the bank requires every answer. Only the newest
 * version does: a draft always reads it, and an older version keeps the rules
 * that its snapshots were submitted under.
 */
function requiresEveryAnswer(bank: ClientApplicationBank, version: string): boolean {
    return (
        bank.requireEveryAnswer === true && clientApplicationBankVersions(bank).at(-1) === version
    );
}

/** Whether a question of a bank that requires every answer takes a "Not applicable" tick. */
function takesNotApplicable(question: ClientApplicationQuestion): boolean {
    if (question.notApplicable === true) return true;
    if (question.optional === true) return false;
    if (NEVER_REQUIRED_KINDS.has(question.kind) || question.kind === 'acknowledgement')
        return false;
    return isClientApplicationNotApplicableQuestion(question.id);
}

function versionView(version: string, bank: ClientApplicationBank): VersionView {
    let views = VERSION_VIEWS.get(bank);
    if (views === undefined) {
        views = new Map();
        VERSION_VIEWS.set(bank, views);
    }
    const cached = views.get(version);
    if (cached !== undefined) return cached;
    const versions = clientApplicationBankVersions(bank);
    if (!versions.includes(version)) {
        throw new Error(`"${version}" is not a version of the form (${versions.join(', ')})`);
    }
    const sections: ClientApplicationSection[] = [];
    for (const section of bank.sections) {
        const questions = section.questions
            .filter((question) => isClientApplicationQuestionInVersion(question, version, versions))
            .flatMap((question) =>
                completedQuestion(questionInVersion(question, version), bank, version),
            );
        // A section that no question of this version fills is not part of the
        // version. A section that holds no question in any version stays.
        if (questions.length === 0 && section.questions.length > 0) continue;
        const unchanged =
            questions.length === section.questions.length &&
            questions.every((question, index) => question === section.questions[index]);
        sections.push(unchanged ? section : { ...section, questions });
    }
    const view: VersionView = {
        sections,
        byId: new Map(
            sections
                .flatMap((section) => section.questions)
                .map((question) => [question.id, question]),
        ),
    };
    views.set(version, view);
    return view;
}

/**
 * The sections of one version of the form, with the words and the conditions
 * of that version. The default is the current version.
 */
export function clientApplicationSections(
    version: string = CLIENT_APPLICATION_FORM_VERSION,
    bank: ClientApplicationBank = CLIENT_APPLICATION_BANK,
): readonly ClientApplicationSection[] {
    return versionView(version, bank).sections;
}

/**
 * Each question id of the bank, in each version, in the order the form
 * prints them. A retired id stays here for ever.
 */
export function clientApplicationBankQuestionIds(
    bank: ClientApplicationBank = CLIENT_APPLICATION_BANK,
): readonly string[] {
    const current = clientApplicationBankVersions(bank).at(-1);
    const ticks =
        current === undefined || !requiresEveryAnswer(bank, current)
            ? new Set<string>()
            : new Set(
                  versionView(current, bank)
                      .sections.flatMap((section) => section.questions)
                      .flatMap((question) => question.notApplicableFor ?? []),
              );
    return bank.sections.flatMap((section) =>
        section.questions.flatMap((question) =>
            ticks.has(question.id)
                ? [question.id, clientApplicationNotApplicableId(question.id)]
                : [question.id],
        ),
    );
}

/**
 * Every section of the CURRENT version of the form, in the order the form
 * prints them. A draft, the journey and the submission read this view.
 */
export const CLIENT_APPLICATION_SECTIONS: readonly ClientApplicationSection[] =
    clientApplicationSections(CLIENT_APPLICATION_FORM_VERSION);

/** Every question of the current version, in the order the form prints them. */
export const CLIENT_APPLICATION_QUESTIONS: readonly ClientApplicationQuestion[] =
    CLIENT_APPLICATION_SECTIONS.flatMap((section) => section.questions);

/** Every question id of the current version, in the order the form prints them. */
export const CLIENT_APPLICATION_QUESTION_IDS: readonly string[] = CLIENT_APPLICATION_QUESTIONS.map(
    (question) => question.id,
);

/**
 * Find one section of a version, or undefined when the id is unknown. The
 * form is the individual form unless the caller gives one (SEP-293).
 */
export function clientApplicationSection(
    id: string,
    version?: string,
    form?: ClientApplicationFormDefinition,
): ClientApplicationSection | undefined {
    const context: ClientApplicationFormContext = { version, form };
    return clientApplicationSections(
        clientApplicationContextVersion(context),
        clientApplicationContextBank(context),
    ).find((section) => section.id === id);
}

/**
 * Find one question of a version, or undefined when that version does not
 * hold the id. The form is the individual form unless the caller gives one
 * (SEP-293).
 */
export function clientApplicationQuestion(
    id: string,
    version?: string,
    form?: ClientApplicationFormDefinition,
): ClientApplicationQuestion | undefined {
    const context: ClientApplicationFormContext = { version, form };
    return versionView(
        clientApplicationContextVersion(context),
        clientApplicationContextBank(context),
    ).byId.get(id);
}

/**
 * The text format of a `short_text` question, given the answers so far
 * (SEP-293, rule H12). The format of `textFormatWhen` applies while its
 * condition holds. Otherwise the format is `textFormat`.
 */
export function clientApplicationTextFormat(
    question: ClientApplicationQuestion,
    answers: Readonly<Record<string, unknown>>,
): ClientApplicationTextFormat | undefined {
    const when = question.textFormatWhen;
    if (when !== undefined && matchesCondition(when, answers)) return when.format;
    return question.textFormat;
}

/** The conditions of a question, as a list. A list is an AND. */
export function clientApplicationConditions(
    question: ClientApplicationQuestion,
): readonly ClientApplicationCondition[] {
    const showWhen = question.showWhen;
    if (showWhen === undefined) return [];
    return isConditionList(showWhen) ? showWhen : [showWhen];
}

function isConditionList(
    showWhen: ClientApplicationShowWhen,
): showWhen is readonly ClientApplicationCondition[] {
    return Array.isArray(showWhen);
}

/**
 * Whether a question is shown, given the answers the client has given.
 *
 * The question must be in the version (the default is the current version).
 * Then each condition of its `showWhen` must hold, and its `showWhenRule`
 * must hold. A condition copies `isQuestionVisible` of the fact find: a
 * STRING answer matches when `equals` holds it, and an ARRAY answer matches
 * when it INCLUDES any entry of `equals`. A hidden question never enters a
 * save payload and never counts as answered, so a stale stored answer under
 * a closed branch is inert.
 *
 * The rule is transitive: a question is hidden when a question that it reads
 * is hidden. Thus a stale answer under a closed parent cannot open a
 * grandchild. For example, row 3 of tax residency closes when the client
 * closes row 2, although `has_tax_residency_3` still holds Yes (SEP-286). A
 * parent that the version does not hold is hidden, so its child is hidden
 * too.
 */
export function isClientApplicationQuestionVisible(
    question: ClientApplicationQuestion,
    answers: Readonly<Record<string, unknown>>,
    context: ClientApplicationFormContext = {},
): boolean {
    const version = clientApplicationContextVersion(context);
    const bank = clientApplicationContextBank(context);
    const rules: ClientApplicationRuleContext = { version, today: context.today ?? null };
    return isVisibleById(question.id, answers, versionView(version, bank).byId, bank, rules, 0);
}

function isVisibleById(
    id: string,
    answers: Readonly<Record<string, unknown>>,
    byId: ReadonlyMap<string, ClientApplicationQuestion>,
    bank: ClientApplicationBank,
    rules: ClientApplicationRuleContext,
    depth: number,
): boolean {
    // The bank is a few conditions deep. The limit stops a loop if a future
    // edit makes a cycle.
    if (depth >= 16) return true;
    const question = byId.get(id);
    if (question === undefined) return false;
    const conditions = clientApplicationConditions(question);
    if (!conditions.every((condition) => matchesCondition(condition, answers))) return false;
    if (question.showWhenRule !== undefined) {
        const rule = bank.visibilityRules[question.showWhenRule];
        if (rule === undefined || !rule(answers, rules)) return false;
    }
    return conditions.every((condition) =>
        isVisibleById(condition.questionId, answers, byId, bank, rules, depth + 1),
    );
}

/** Whether the answers match one condition, and only that one. */
function matchesCondition(
    condition: Pick<ClientApplicationCondition, 'questionId' | 'equals'>,
    answers: Readonly<Record<string, unknown>>,
): boolean {
    const answer = answers[condition.questionId];
    if (Array.isArray(answer)) {
        return condition.equals.some((value) => answer.includes(value));
    }
    if (typeof answer === 'string') {
        return condition.equals.includes(answer);
    }
    return false;
}

/**
 * Whether a question needs an answer, given the answers the client has given.
 *
 * A question is required when the bank marks it `required` and the client has
 * not ticked the question that {@link ClientApplicationQuestion.requiredUnlessTicked}
 * names, or its "Not applicable" tick. Visibility is a separate rule: a hidden question is never required,
 * and the caller checks {@link isClientApplicationQuestionVisible} as well.
 */
export function isClientApplicationQuestionRequired(
    question: ClientApplicationQuestion,
    answers: Readonly<Record<string, unknown>>,
): boolean {
    if (question.required !== true) return false;
    const waiver =
        question.requiredUnlessTicked ??
        (question.notApplicable === true
            ? clientApplicationNotApplicableId(question.id)
            : undefined);
    return waiver === undefined || answers[waiver] !== true;
}

/**
 * The paragraphs of explanation that one question prints after its
 * explainer, given the answers so far (SEP-293.8).
 *
 * A paragraph prints when each condition of its `showWhen` holds. The
 * question itself must already be visible: this function reads the
 * conditions of the paragraph only.
 */
export function clientApplicationExplainerParagraphs(
    question: ClientApplicationQuestion,
    answers: Readonly<Record<string, unknown>>,
): readonly string[] {
    return (question.explainerParagraphs ?? [])
        .filter((paragraph) => {
            if (paragraph.showWhen === undefined) return true;
            const conditions = isConditionList(paragraph.showWhen)
                ? paragraph.showWhen
                : [paragraph.showWhen];
            return conditions.every((condition) => matchesCondition(condition, answers));
        })
        .map((paragraph) => paragraph.text);
}

/**
 * The chosen options of a `multi_select` that the client cannot remove now,
 * because another chosen option brings them with it (SEP-293.7).
 */
export function lockedClientApplicationOptions(
    question: ClientApplicationQuestion,
    chosen: readonly string[],
): readonly string[] {
    const implied = question.impliedOptions;
    if (implied === undefined) return [];
    const locked = new Set<string>();
    for (const option of chosen) {
        for (const brought of implied[option] ?? []) {
            if (brought !== option && chosen.includes(brought)) locked.add(brought);
        }
    }
    return (question.options ?? []).filter((option) => locked.has(option));
}

/** The notices that the journey prints under a control for the chosen options (SEP-293.7). */
export function clientApplicationChosenOptionNotices(
    question: ClientApplicationQuestion,
    chosen: readonly string[],
): readonly string[] {
    const notices = question.chosenOptionNotices;
    if (notices === undefined) return [];
    return (question.options ?? [])
        .filter((option) => chosen.includes(option) && notices[option] !== undefined)
        .map((option) => notices[option]!);
}
